/*
 * vms-elevation-scene.js
 * ---------------------------------------------------------------------------
 * The interactive picture of a ceiling mount: structure at the top, ceiling
 * plate, the real composited pipe mast, the drop ceiling line, the mounting head
 * and a see-through TV. Two of those are draggable, the head (which carries the
 * TV with it) and the tile line.
 *
 * Standalone on purpose, exactly like vms-mast-art.js: window.VMSElevationScene
 * knows nothing about the configurator's state object, its solver or its DOM ids.
 * It is handed a model of inches and hands back gestures. Anything that has to
 * know about SKUs, pricing or the URL lives in the caller.
 *
 * THE SINGLE-VALUE RULE, AND IT IS THE LOAD-BEARING DECISION IN THIS FILE
 * The callbacks emit RAW, UNROUNDED inches straight off the pointer or the key
 * gesture. This module never rounds, never clamps and never quantises a value it
 * emits. The caller clamps (VMSElevation.clampDrop), resolves (resolveDrop) and
 * quantises (VMSElevation.quantise) exactly ONCE, then calls update() with the
 * final authoritative number, and every pixel here is positioned purely from
 * that.
 *
 * [WARN] Do not "helpfully" round in here as well, in either direction. Two
 * rounding grids in one feature is precisely the defect section 7.2 of
 * SPEC-drag-elevation-2026-07-26.md condemns: Sanus quantises TV heights to a
 * quarter inch and hole positions to an eighth, so their own screen prints two
 * numbers for one position that disagree by 1/8 in. There is exactly one
 * quantiser in this system and it is not this file.
 *
 * THE CALLER OWNS THE LIMITS TOO, NOT JUST THE VALUES
 * Same reasoning one step further. update()'s model must carry the ENFORCED,
 * already-quantised stops for both handles: dropMinEnforced, dropMaxEnforced,
 * tileMinEnforced and tileMaxEnforced. Those, and only those, drive
 * aria-valuemin, aria-valuemax, Home, End and the at-limit detection. The raw
 * geometric model.minDrop stays in the model because model.minDropReason is
 * load-bearing for the copy, but it is NOT a stop: it is 0.216 in below the
 * enforced one in the worked example and up to half an inch below it in
 * general, which is enough to make "we are at the limit" never true and the
 * explanation dead code. See the [WARN] on limits() below.
 *
 * A drag is also ABSOLUTE, never incremental: the pointer position is converted
 * to inches from the value captured at pointerdown, not accumulated from the
 * previous frame. That is what makes a clamped or bumped value stable instead of
 * creeping an inch per pointermove.
 *
 * ONE PX PER INCH, BOTH AXES
 * Vertical depth and horizontal TV width share one scale, derived once per
 * layout from whichever of the height budget and the container width binds
 * first. Sanus derives the two axes separately (canvas height for depth, an 856
 * px divisor against an 875 px canvas for width) and their art is 2.2 percent
 * oversized as a result, so their drawn screen bottom sits about 0.7 in from
 * where their own label says it is.
 *
 * THE SCREEN RUNS OFF THE SIDES AND IS CROPPED, AT EVERY WIDTH
 * Owner decision 2026-07-27, and it is the second half of the one-scale rule
 * rather than an exception to it: the TV is still drawn at exactly the scene
 * scale, it is simply allowed to be bigger than the view. What changed is that
 * its width no longer gets a VOTE on that scale. A 65 in screen is 56.65 in
 * wide, so on a 313 px phone stage it was pinning px per inch at about 5.4 where
 * the height budget allowed 10.6: the drop, the tile line and the mast length,
 * which are the whole point of an elevation, drew at half size to buy screen
 * width nobody is measuring. Cropping the sides costs nothing that matters and
 * roughly doubles the drawing.
 *
 * ONE RULE AT EVERY WIDTH, not a mobile-only one, and that is deliberate. The
 * binding is a function of the STAGE width, not of a device class: this block
 * sits inside a page grid that squeezes it to about 271 px at a 1000 px viewport
 * and about 455 px between 900 and 1200, so mid desktop was width bound just as
 * hard as a phone. Worse, a breakpoint-conditional rule would draw the same build
 * at two different scales either side of 899.98 px, with the screen cropped on
 * one side and whole on the other, for no reason a customer could see. A second,
 * width-conditional scale rule in this file is precisely the two-scales defect
 * section 7.2 of the spec condemns. At 1440 px the change is small (a 65 in
 * screen crops by roughly 15 px a side), and the same edge treatment covers it.
 *
 * See cropTV() for how a cropped screen is made to read as deliberate.
 *
 * [WARN] THE FILL AND THE RULES ARE TWO ELEMENTS, AND THAT SPLIT IS THE WHOLE
 * CROP CUE. The fade is a mask and a mask applies to the ENTIRE element, borders
 * included. On one element it therefore dissolved the top and bottom rules at
 * precisely the two edges where the crop needs explaining, leaving nothing that
 * says "screen" anywhere near them. A browser pass on 2026-07-27 read the result
 * as an unexplained grey wash over the lower half of the picture, at 375 px and
 * at 1440 px alike. So the fill is its own child span and carries the mask alone,
 * while the rules stay on the parent and run CLEAN OUT of both sides of the
 * frame. Do not put the fade back on the parent.
 *
 * THE BAND IS ALSO NAMED, for the same reason. At 65 in on a 375 px phone the
 * screen is 603 px wide inside a 308 px stage: no left edge, no right edge, no
 * corner, nothing to recognise it by. So a quiet caption sits inside the bottom
 * rule saying what it is. The size it prints is the NOMINAL one off the model
 * (model.tvSize); see nameTV() for why that stopped being derivable from the drawn
 * rectangle on 2026-07-28. It is display only, like fmt().
 *
 * WHAT THE RECTANGLE IS: THE WHOLE TV, NOT THE PICTURE (owner correction 2026-07-28)
 * The model hands this file drawn extents and this file draws them, which is why the
 * change is nearly invisible in here. What moved is on the other side of that line:
 * tvHeight and tvWidth are now ONE 16:9 rectangle computed from the physical TV
 * height (the 16:9 screen plus the owner's 1 in bezel allowance), where they used to
 * be the bare 16:9 screen. The drawn TV is therefore an inch taller and about 1.8 in
 * wider than it was, and it now lands EXACTLY on the limits the at-limit sentences
 * and the readout describe, instead of sitting half an inch clear of them with
 * nothing on screen to explain the gap. The rectangle is still centred on the head,
 * because the allowance is still split evenly. See assets/vms-elevation.js's header
 * for the ruling and the numbers, which did not move.
 *
 * [WARN] VMSMastArt.render DERIVES ITS OWN PX PER INCH AND CAPS IT AT 22.
 * It computes ppi = Math.min(lengthPx / totalIn, maxPpi) and then sets the canvas
 * CSS size itself. Handing it a length alone is not enough: the cap stays in play
 * and the pipe silently draws at a different scale from the tile line and the TV
 * around it. Whatever length you want, maxPpi has to be the ppi that length
 * implies, or the cap quietly overrides you.
 *
 * THE MAST IS DRAWN TO THE DISTANCE BETWEEN THE PLATE AND THE HEAD, NOT TO ITS
 * OWN NOMINAL LENGTH. Owner ruling 2026-08-20. THIS REVERSES WHAT THIS COMMENT
 * USED TO SAY, and the old reasoning is kept here because it is not stupid and
 * somebody will re-derive it otherwise: totalInches(spec) is the mast length from
 * the BILL OF MATERIALS, the canvas was anchored at pipeTop and allowed to take
 * that natural length, and stretching it to reach pipeBottom was rejected on the
 * grounds that it puts a second scale into the drawing.
 *
 * What that missed is that the spec length and the geometric span are NOT the same
 * number, and the difference has to land somewhere visible. buildMasts accepts a
 * build whose reachable range misses the mast target by up to TOL (0.5 in), and
 * the drawing spec is then CLAMPED to that range, so the parts list can be up to
 * half an inch shorter than the distance the pipe has to cover. Anchored at the
 * top, every bit of that lands as a hole above the head. Most heads swallow it
 * because the pipe runs inches into their render, but 22HE001 and 22HE021 take
 * only about 0.2 in of pipe, so the hole is visible: measured at 0.298 in, and
 * reported by the owner at drops of 13, 44 and 49.5 with 11UN001 + EXT2036.
 *
 * So: the plate is pinned to the ceiling, the head is pinned to the drop, and the
 * pipe fills the space between them, which is what a real pipe does. The second
 * scale the old rule feared is real but it is bounded by that same tolerance, so
 * it is at most about 1.4 percent, roughly 0.2 px of girth. A fifth of a pixel of
 * girth against a third of an inch of daylight is not a close call.
 *
 * The head could NOT be un-pinned and simply hung below the pipe, which is the
 * other way to close the joint: the TV rectangle is centred on the head and the
 * readout's two depths are derived from the same drop, so a head that floats to
 * wherever the parts end would disagree with the number the customer typed by up
 * to half an inch, and the handle would slip under the pointer during a drag.
 *
 * THE SCALE FREEZES FOR THE LENGTH OF A GESTURE, AND THE PIPE GOES PLAIN
 * px per inch is derived from sceneDepth, and sceneDepth changes on EVERY
 * pointermove, because the drop is one of the depths it is the maximum of. So
 * without a freeze the TV, the plate and the pipe girth all rescale under the
 * hand while the pipe LENGTH does not: the composited canvas is memoised on the
 * mast spec, and the spec only changes when the caller re-solves, which it does
 * on commit and not on move. Two update rates in one picture, and the length is
 * the one that lags and then snaps. Reported from real use on the dev theme,
 * 2026-07-26: "as a user it is disorienting".
 *
 * The owner's call: LOCK ppi and the stage height at the start of the gesture, at
 * the values ALREADY IN USE so nothing jumps at the moment of grabbing, and swap
 * the composited mast for the plain pipe, which spans plate bottom to pipe bottom
 * and follows the value with zero lag. On release the lock clears, ppi is
 * recomputed from the fresh model and the real artwork comes back.
 *
 * [WARN] THE PLAIN PIPE HAS TO CARRY THE COMPOSITED MAST'S OWN WEIGHT, or the
 * freeze reads as a failure instead of a simplification. It drew at a flat 28
 * percent black until 2026-07-27, against a composited mast that is fully opaque
 * and averages about 52 of 255, so grabbing the mount turned a crisp technical
 * illustration into a pale skeleton in one frame, on the same frame that the part
 * labels all vanish. Browser pass 2026-07-27: "it looks less like simplified
 * while you drag and more like the drawing lost its rendering." The stops are now
 * measured off the sprite itself, in .vmscfg-scene__pipe in
 * assets/vms-configurator.css. Note the same element is the PERMANENT fallback
 * for a sprite that fails to load, so it has to look right standing still and not
 * only for the length of a gesture.
 *
 * [WARN] THE FROZEN SCALE IS WHAT KEEPS THE HANDLE UNDER THE FINGER. The emitted
 * value is startValue + (clientY - startY) / ppi and the handle is drawn at
 * value * ppi. Those two are inverses of each other only while ppi is CONSTANT,
 * which a live-derived ppi is not: a moving scale makes the drawn handle drift
 * away from the pointer as the value grows. There must be exactly ONE ppi in the
 * drag path. Do not introduce a second scale for the plain pipe, for the labels,
 * or for anything else that moves during a gesture.
 *
 * [WARN] A FROZEN SCALE MEANS THE PICTURE CAN OUTGROW THE BOX. With the scale
 * held still, half a TV can end up below the stage edge on a long drag. The stage
 * CLIPS, and its height is frozen for the gesture too, so a drag can never change
 * the page layout. Do not "fix" a clipped TV by letting the stage grow during a
 * gesture: that is the disorienting reflow this whole freeze exists to remove.
 *
 * FREEZING MUST NOT ROUND. The lock changes the SCALE, never the value: move()
 * still emits raw unrounded inches off the pointer. See the single-value rule.
 *
 * THE HEIGHT BUDGET IS READ FROM CSS, NOT FROM THE CALL SITE
 * The vertical budget comes from the --vmscfg-scene-h custom property on the
 * scene root, so the mobile height lives next to the mobile breakpoint in
 * vms-configurator.css instead of being a magic number in a mount() call the
 * stylesheet cannot see. opts.maxHeightPx still works and is now an UPPER BOUND
 * on whatever CSS asks for, so an existing call site keeps its meaning. With
 * neither, DEFAULT_HEIGHT_PX applies. The read is cached against the measured
 * width and skipped entirely while the scale is locked, because
 * getComputedStyle forces a style recalculation and layout() runs per
 * pointermove.
 *
 * PART LABELS: THE SIDE SPLIT IS THE COLLISION STRATEGY, PUSH APART IS THE
 * FALLBACK. Owner request: name the parts on the drawing. Pipes and couplers
 * strictly ALTERNATE down a mast, so couplers on the left and pipes on the right
 * means two vertically adjacent labels are never on the same side: each one gets
 * a whole pipe length of room rather than half of one, and the common case needs
 * no displacement at all. The plate and the head ride with the couplers on the
 * left, because their nearest same-side neighbour is then a coupler a full pipe
 * away, where on the right they would sit half a pipe from the first and last
 * pipe labels. What is left for the push-apart pass is only pipe against pipe and
 * coupler against coupler, which happens at low px per inch on a short stack.
 *
 * The wording is GENERIC on purpose. SKUs and real part lengths are not in the
 * model this module is handed, the parts table in the summary panel already
 * prints both, and inventing them here would be a second source of truth.
 *
 * THE CHIP WIDTH IS MEASURED AND THE WORDING STEPS DOWN TO FIT IT. A truncated
 * label helps nobody, and until 2026-07-27 this file guessed twice over: the chip
 * was a flat 28 percent of the stage and the wording was picked off a stage-width
 * breakpoint, so the two only ever agreed by luck. They did not agree. "Your
 * mounting head" needed 124 px inside a 122 px chip and ellipsised at EVERY
 * width, 1440 included, and below a 420 px stage the short forms overflowed too.
 * Now each part carries its wordings longest first, every chip is measured, and
 * any chip that does not fit steps down a form and is re-measured until it fits or
 * runs out of forms, at which point it is DROPPED rather than ellipsised.
 *
 * [2026-08-23] "FITS" MEANS ONE LINE INSIDE THE CAP, OR WRAPPED TO TWO LINES
 * INSIDE IT. Owner: "You could consider wraping the descriptive text to 2 lines."
 * The cap is still a WIDTH, but wrapping trades width for height, so a form that
 * overran the cap on one line can still fit if it breaks across two lines with no
 * single WORD wider than the cap. That is what puts a SKU form ("33HE001 Mounting
 * head", 152 px on one line) onto a phone whose cap is about 99 px: it wraps to
 * "33HE001" over "Mounting head". A form needing three lines, or holding a word
 * wider than the cap, still steps down. Chips are one line OR two now, so their
 * heights are measured PER CHIP and the vertical collision passes space and drop
 * on each chip's own height. The stylesheet keeps text-overflow: ellipsis as a
 * last-ditch valve for a root font size this file cannot predict; nothing reaches
 * it, because a form that cannot wrap to two lines is dropped, never truncated.
 *
 * THE CAP IS THE REAL GEOMETRY, PER SIDE, not a fraction of the stage. What the
 * old fraction stood in for is this: the stage edge inset, the chip, its clear
 * air, the shortest leader still worth drawing, the tip gap and the widest thing
 * that side's arrows must stop clear of all have to fit between the edge and the
 * centre line. Computing it directly is why a 1440 px stage can now afford a 126
 * px chip where the fraction allowed 127 but the code only asked for 124, and why
 * a 320 px phone correctly refuses one. The two sides differ a lot: the left
 * clears the ceiling plate and the head block, the right only a 1.936 in pipe.
 * (Before 2026-08-23 the head's "Drag NN in" chip was centred and the left had to
 * clear it too; it is right-justified now, so it left the left cap and became a
 * right-gutter HANDLE that pipe labels are dropped past, never a cap term.)
 *
 * [WARN] THE PLATE AND THE HEAD STEP DOWN TOGETHER. The owner asked for "your
 * chosen ceiling plate" and "your chosen mounting head", a matched pair, and one
 * of them in the long form beside the other in the short form reads as a mistake
 * rather than as a fit decision. They are flagged as a pair and take the longest
 * form that suits BOTH.
 *
 * SCHEMATIC BLOCKS, NEVER PRODUCT PHOTOS
 * The plate and the head are plain blocks. The photos are 3/4 angle perspective
 * shots and the pipe art is orthographic, so mixing them reads as a collage.
 * Settled 2026-07-26. Photos stay in the summary panel as identification.
 *
 * THE TV IS A SIBLING, NOT AN ANCESTOR
 * The see-through fill is its own element at about 10 percent black. The head
 * block and the grips are siblings drawn after it, so they stay fully opaque with
 * no blend modes and nothing to get wrong. The transparent drag wrapper around
 * both is not a fill and carries no opacity of its own.
 *
 * [WARN] TOUCH: THIS MUST NEVER BECOME A SCROLL TRAP.
 * The head is a big object in the middle of a phone screen and its drag axis is
 * the same axis the page scrolls on, so "is this a drag or a scroll" cannot be
 * answered by direction. It is answered by INTENT instead: a touch has to rest on
 * the handle for TOUCH_HOLD_MS without travelling more than TOUCH_SLOP_PX before
 * the gesture is claimed. A flick scrolls the page as normal and we never see it
 * again. Only once the gesture is claimed does touch-action get set to none on
 * that one handle and does the non-passive touchmove listener start calling
 * preventDefault. Claiming inside the browser's own touch slop is what makes the
 * preventDefault effective. Do not move touch-action:none into the static CSS as
 * a convenience: that reintroduces the trap for every touch that was only ever
 * trying to scroll past the drawing.
 *
 * KEYBOARD DIRECTION, a deliberate call. Every depth in this feature grows
 * DOWNWARDS, so ArrowDown makes the number bigger and moves the object down.
 * That is spatially honest and matches the drag, and it is the reverse of the
 * usual "up increases" convention for a vertical slider. aria-valuetext always
 * states the number in inches, so nothing rests on guessing the direction.
 *
 * ANNOUNCE ON RELEASE, NEVER PER MOVE. The visually hidden live region here is
 * written on pointerup, on keyup and on Home or End only. The scene is also
 * mounted OUTSIDE the configurator's #s3out, which is both rebuilt by innerHTML
 * on every keystroke and carries aria-live of its own.
 */
(function () {
  'use strict';

  var NS = 'vmscfg-scene';

  // ---- scale and layout tunables -------------------------------------------
  var DEFAULT_HEIGHT_PX = 380;  // vertical budget, used only when CSS says nothing
  var HEIGHT_VAR = '--vmscfg-scene-h';  // where CSS states the budget
  var MIN_HEIGHT_PX = 200;      // rails on the CSS value, so a stylesheet typo
  var MAX_HEIGHT_PX = 900;      // cannot produce a 4 px or a 4000 px drawing
  var TOP_PAD_PX = 14;          // hatch band drawn ABOVE depth 0
  var SIDE_PAD_PX = 6;
  var BOTTOM_PAD_IN = 2;        // breathing room under the deepest thing drawn
  var MAX_PPI = 24;             // a 10 in scene should not draw a comically fat pipe
  var MIN_PPI = 0.5;
  var FALLBACK_DEPTH_IN = 24;   // used ONLY when sceneDepth is null
  var MIN_DEPTH_IN = 12;

  // ---- break line tunables --------------------------------------------------
  // [RULE] NOTHING MEASURED LIVES INSIDE THE REMOVED BAND. A long mast starves
  // everything else because one px-per-inch has to fit the whole length, so a run
  // of PLAIN pipe is cut out and marked with a break glyph. The band is chosen so
  // the plate, the head, the TV, the tile line, every coupler joint and both ends
  // of the pipe it cuts stay OUTSIDE it. See SPEC-mast-break-2026-09-06.md.
  var BREAK_PPI_MIN = 6.5;      // at or above this the drawing is legible unbroken
  var BREAK_PPI_TARGET = 9;     // aim to redraw a broken mast at about this scale,
                                // which is where a typical build already draws today
  var MIN_BREAK_IN = 8;         // a shorter removed run costs more than it saves: a
                                // long-but-crowded mast (couplers, a tile line) stays whole
  // [RULE] THE GAP IS A FIXED PIXEL HEIGHT, NEVER AN INCH VALUE. It is a symbol,
  // not a measurement, so it must read the same at every scale. The scene's y()
  // maps the band onto exactly this many px and yInv() inverts it.
  var GLYPH_PX = 28;
  var STUB_IN = 3;              // pipe left showing at each cut end, so both cut ends
                               // read as pipe and every coupler joint keeps pipe on both sides
  var TILE_CLEAR_IN = 2.5;     // keep the tile line (it carries a chip and a label) on real pipe
  var TV_CLEAR_IN = 1;         // the band must end at least this far ABOVE the drawn TV top,
                               // and the head's drawn top is kept clear the same way
  // [WARN] THE WIDTH BUDGET, AND THE TV IS NOT IN IT. This is the width the scale
  // has to fit, in inches: the ceiling plate at 8 in is the widest thing that must
  // be seen whole, and a 16 in budget holds it to half the stage. It used to be a
  // FLOOR under the TV's own width, which is what made the screen bind the scale.
  // See the crop decision in the file header before changing it.
  var FIT_WIDTH_IN = 16;
  // How far the cropped screen's FILL fades before the stage edge. Held at 18 px
  // through the 2026-07-27 rework on purpose. Its job changed: it used to be the
  // only crop cue and it dissolved the rules doing it, where now the rules run out
  // of the frame crisp and the fade only has to stop the fill ending on a hard
  // vertical cut. A shorter fade would put that cut back, and a longer one starts
  // to look like the band tapering rather than continuing. 18 px is about 6
  // percent of a 308 px phone stage and 4 percent of a 453 px desktop one.
  var TV_FADE_PX = 18;
  // The caption naming the screen. Both floors exist because layoutHead() runs on
  // EVERY pointermove and may not measure anything: these mirror the CSS instead.
  // [WARN] MIRRORS .vmscfg-scene__tvname and .vmscfg-scene__grip in
  // vms-configurator.css. If either changes size, these drift.
  var TV_NAME_H_PX = 15;        // the caption's 1.5rem line box
  var TV_NAME_INSET_PX = 3;     // its 0.3rem lift off the bottom rule
  var TV_NAME_CLEAR_PX = 6;     // air between the caption and the head furniture
  var GRIP_H_PX = 22;           // the grip chip: 1.6rem line, 0.2rem padding, borders
  var TV_NAME_MIN_W_PX = 150;   // narrower visible band than this and it is clutter

  // Schematic block sizes in inches. Representative, not per SKU: these are
  // drawings of "a plate" and "a head", and the real dimensions live in the parts
  // table where a customer can read them.
  var PLATE_W_IN = 8;
  var HEAD_W_IN = 5;
  var HEAD_H_IN = 3;
  var PIPE_OD_IN = 1.936;       // matches vms-mast-art's sprite band, for the fallback pipe

  // [WARN] THE DRAG-TIME BREAK GLYPH MIRRORS vms-mast-art's canvas glyph. The
  // composited mast draws its break in a canvas (VMSMastArt.render, breakBands); the
  // frozen plain pipe drawn during a gesture draws the SAME glyph in CSS/SVG so
  // nothing jumps at grab or release. These are the copies that keep the two in
  // step (owner rev 2.1: the pipe runs through, only the diagonal band is blank).
  // If vms-mast-art's BREAK_* change, change these too.
  var BREAK_MARK_SPAN = 1.6;           // each line spans this many girths across (0.8 either side)
  var BREAK_MARK_SLANT = 30 * Math.PI / 180;  // each line's lean off the across (girth) axis
  var BREAK_BAND_PX = 10;              // width of the blank diagonal band, along the length axis

  // Ortho part art. The cutouts are delivered at a native 94 px per inch, which
  // is what turns their intrinsic pixel size into real inches.
  var ART_NATIVE_PPI = 94;
  // Owner ruling 2026-08-19: every ceiling plate and clamp adapter overlays the
  // top of the pipe by a flat 0.350 in. This is the ONE placement constant this
  // file needs; the head needs none at all, because its own image centre is the
  // hole pattern. Both facts and the reasoning behind them live in
  // assets/vms-assembly-art.js and scripts/ortho-cutouts/placement.py. Keep the
  // three in step: a change here that is not made there is a silent divergence.
  var ART_EDGE_OVERLAY_IN = 0.350;
  // How far the drawn mast may be stretched or squeezed off its own nominal length
  // to span the distance between the plate and the head. The real residual is
  // bounded by the solver's own tolerance: buildMasts accepts a build whose range
  // misses the mast target by up to TOL (0.5 in) and specFromBuild then quantises
  // the telescoping setting to 0.5 in, so about 0.75 in is the worst case the data
  // can produce. This is a sanity net, not a working limit: past it something has
  // gone wrong upstream and drawing a visibly distorted pipe would hide the fault,
  // so the mast falls back to its own natural length and the joint shows.
  var MAST_STRETCH_MAX_IN = 1.5;
  // [WARN] MIRRORS vms-mast-art's coupler sprite (249 x 210 at 94 px per inch).
  // It is not exported, and it is only ever used to decide where a leader arrow
  // stops short of the drawn coupler band. If the sprite changes, this drifts.
  var COUPLER_OD_IN = 2.234;

  // ---- part label tunables --------------------------------------------------
  var LABEL_EDGE_PX = 4;        // chip inset from the stage edge, matches the CSS
  var LABEL_LEAD_PAD = 3;       // clear air between the chip and its leader
  var LABEL_TIP_GAP_PX = 3;     // the arrow stops this far short of the part
  // A ceiling on the chip, not the chip itself: the width now comes from measuring
  // the words. This only stops a wide stage from spending an absurd amount on a
  // chip; the REAL clearance rule is the geometric term in gutterCap, which is what
  // governs every narrow stage and is not touched by this number.
  //
  // [WARN] THIS CONSTANT SILENTLY DECIDES WHICH WORDINGS CAN EVER APPEAR. At 140 it
  // was below the natural width of most of the SKU-named forms added 2026-08-20 -
  // every head form in the pool measures 151 to 152 px, because the wording is
  // always a 7 character SKU plus "Mounting head" - so those forms could not render
  // at ANY viewport width on ANY device, and the ladder just stepped past them to
  // the generic wording. Nothing warned; the labels simply looked like the change
  // had not shipped. Worse, plate and head step down as a pair, so the head's
  // unavoidable failure dragged the plate down with it and no plate could show its
  // SKU either, including ones whose own form fitted comfortably.
  // Raised to 190 to clear the widest form in the pool (the telescoping range at
  // 185 px, then an angled plate at 179). MEASURE BEFORE CHANGING IT: the natural
  // widths are in the 2026-08-20 label sweep, and a wording longer than this is
  // invisible rather than merely tight.
  // [NOTE] A wider gutter does NOT shrink the drawing. ppi comes from byWidth,
  // which is stageW minus SIDE_PAD_PX only and knows nothing about gutters, so the
  // scale of the mount is unchanged by this. What a wider chip does do is float
  // further over the TV band, which is allowed by the same owner decision that lets
  // the TV run off both sides of the stage and be cropped: the screen is deliberately
  // not a collision target for anything.
  var LABEL_GUTTER_MAX = 190;
  var LABEL_GAP_PX = 3;         // clear air between two stacked chips
  var LABEL_MAX_SHIFT_PX = 48;  // past this a leader points somewhere confusing
  var LABEL_LEAD_MIN_PX = 8;    // a shorter leader than this is not a leader
  // Wordings per part, longest first. ONE MORE THAN THE LONGEST FORMS LIST, so the
  // loop always exits on a fresh measurement rather than on the pass count, which
  // would leave a stale width behind. [RULE] RAISE THIS WHENEVER A FORMS LIST GROWS.
  // It went 4 to 5 on 2026-08-20 when the SKU-named form was prepended to the plate
  // and the head, taking the longest list from three wordings to four. Nothing warns
  // you if this is left behind: the labels simply stop one step short of the wording
  // that would have fitted, on exactly the narrow stages where it matters most.
  var LABEL_FORM_PASSES = 5;
  var LABEL_MIN_STAGE_W = 200;  // below this the two gutters swallow the drawing
  var LABEL_COUPLER_MIN_W = 260;// below this the couplers give up their labels first
  // [WARN] MIRRORS the 2.4rem height of .vmscfg-scene__tile in vms-configurator.css.
  // The tile handle's own grip chip sits at the RIGHT end of that row, in the same
  // gutter the pipe labels use, and it is a handle rather than a label so the
  // collision pass may not move it. Labels keep clear of this band instead.
  var TILE_ROW_H_PX = 24;

  // The shortest the dashed drop-ceiling line may become before the left "Drop
  // ceiling" label is dropped. The fallback is DROP-rather-than-crush, the same
  // discipline the part labels use above: when the rule would be squeezed under
  // this, the left label goes and its text moves onto the drag chip instead, so
  // the name is never lost, only relocated. See layoutTile().
  //
  // [MEASURED ON DEV 2026-08-23] the label is 73.83 px and the chip 90.58 px in
  // its "Drag 12 in" form, so at a 12 in tile depth the dashed line runs 672.81
  // px at 1440, 131.31 at 390 and 101.31 at the owner's 360 px supported floor.
  // IT THEREFORE NEVER FALLS BACK AT A SUPPORTED WIDTH on a typical build, which
  // is the intent: the fallback is for the sub-360 old Android tail and for the
  // long values, since the chip widens with the number it prints and a build near
  // the floor crosses over on the value alone.
  //
  // [WARN] THE CROSSOVER VIEWPORT NUMBER IS SCROLLBAR-DEPENDENT AND TWO HONEST
  // MEASUREMENTS OF IT DISAGREE BY EXACTLY 15 px. The label was measured dropping
  // between 314 and 315 px under emulated overlay scrollbars, and between 328 and
  // 329 px in a desktop window carrying a classic 15 px scrollbar gutter. Both are
  // real; the gutter is simply 15 px the row never gets. Only the RULE WIDTH is
  // scrollbar-independent, which is the other reason this gates on the rule and
  // not on a viewport breakpoint. Below 56 px the dashed line stops reading as a
  // line and starts reading as a few loose dashes between two labels, which is
  // what this floor protects against; it is not a guess.
  var TILE_LABEL_MIN_RULE_PX = 56;

  // ---- interaction tunables -------------------------------------------------
  var DEFAULT_STEP = 0.5;       // matches the number field's step attribute
  var COARSE_MULT = 10;         // Shift, PageUp and PageDown
  var TOUCH_HOLD_MS = 130;
  var TOUCH_SLOP_PX = 6;
  // Safety net ONLY, for a keyup that never arrives (focus stolen mid-run, a
  // dropped event). [WARN] It must stay comfortably longer than the operating
  // system's key REPEAT DELAY, which is about 500 ms on a Windows default: a
  // shorter idle would unfreeze between the first press and the first repeat of a
  // held arrow key and flicker the pipe back and forth.
  var KEY_IDLE_MS = 1200;
  var LIMIT_EPS = 0.02;         // "at the stop" tolerance, well under half a step
  var MIN_CLEARANCE = 0.125;    // mirrors VMSElevation.MIN_CLEARANCE for the tile ceiling

  var uid = 0;

  function num(v) { return typeof v === 'number' && !isNaN(v) && isFinite(v) ? v : null; }

  /** Trim a measurement for display. Never used to decide anything, only to print. */
  function fmt(v) {
    var n = num(v);
    if (n == null) return '';
    return String(Math.round(n * 100) / 100);
  }

  /**
   * A measurement with its unit, singularised. "1 inch", not "1 inches": the
   * floor with no drop ceiling is exactly 1 in, so Home lands there and the live
   * region said "1 inches" out loud. Same two decimal display grid as fmt(), so
   * this decides nothing either.
   */
  function inchesText(v) {
    var s = fmt(v);
    if (s === '') return '';
    return s + (s === '1' ? ' inch' : ' inches');
  }

  /**
   * OWNER-EDITABLE COPY, THREADED IN RATHER THAN OWNED HERE (2026-07-28)
   * ---------------------------------------------------------------------------
   * The at-limit sentences below (and one drawing label, tileLabel) are the only
   * strings in this file a customer ever reads, and they are now theme settings
   * on sections/vms-configurator.liquid.
   * The caller (assets/vms-configurator.js) reads the edited values out of the
   * section's JSON copy block and hands them over as opts.copy.
   *
   * [RULE] ONE EDITED STRING, BOTH SURFACES. reasonLines() feeds the VISIBLE
   * reason line under the drawing and the live region that announces on release.
   * Because both read the same copy() call, an edit cannot make the two disagree.
   * Never re-inline one of these sentences at a call site; that is exactly how the
   * spoken and the printed explanation would drift apart.
   *
   * Every key falls back to the exact sentence this file has always shipped, so a
   * missing, partial or blank map renders today's copy byte for byte. Keep these
   * defaults and the schema defaults in sections/vms-configurator.liquid identical.
   */
  var COPY_DEFAULTS = {
    // [NOTE] A LABEL, not a sentence: the name that sits at the left end of the
    // tile row and, at narrow widths, on the drag chip itself. "Drop ceiling" is
    // a deliberate carve-out from the standing "say the length, never say drop"
    // terminology rule: DROP CEILING is standard trade language for the thing and
    // the rule names it as an explicit exception, and the control above this
    // drawing already asks "Is there a drop/suspended ceiling?", so the label and
    // the control agree rather than teaching two names for one thing.
    tileLabel: 'Drop ceiling',
    needTiles: 'We need the distance from the structure down to the tiles. Enter it and we will keep the TV below the drop ceiling.',
    highTvTiles: 'This is as high as the mount can go. The top of the TV has to clear the tiles by at least 1/8 inch.',
    highBelowTiles: 'This is as high as the mount can go. With a drop ceiling the mount has to sit below the tiles. Enter your TV size and we can allow for the height of the TV as well.',
    highPlate: 'This is as high as the mount can go. The top of the TV has to stay just clear of the ceiling.',
    highParts: 'This is as high as the mount can go with the parts you have picked.',
    // [2026-09-22] The horizontal (face-down) head floors, word for word the
    // configurator's held_head_clears_tiles / held_head_clears_structure.
    highHeadTiles: 'The mounting head needs 1/8 inch of clearance under the drop ceiling tiles, so this is as short as the length can go with the tiles at [tiles].',
    highHeadStructure: 'The mounting head needs 1/8 inch of clearance below the ceiling, so this is as short as the length can go.',
    lowParts: 'This is as low as the mount can go with the parts you have picked.',
    tileMin: 'The tiles cannot sit above the structure.',
    tileMaxTv: 'That is as far down as the tile line goes for a TV this size. Past [tiles], the length that would still hang the TV below your tiles is longer than [length], which is the longest length this page works out. If your tiles really do sit that deep, call us and we will work the length out with you.',
    // [2026-09-22 round 3] The deepest tile line for a horizontal (face-down) head,
    // whose own top (not the TV) is what the tiles must clear. Mirrors tileMaxTv
    // (round 4): [tiles] is the cap, [length] the longest length (200 in).
    tileMaxHead: 'That is as far down as the tile line goes with this mounting head. Past [tiles], the length that would still keep the mounting head 1/8 inch below your tiles is longer than [length], which is the longest length this page works out. If your tiles really do sit that deep, call us and we will work the length out with you.',
    tileMaxPlain: 'That is as far down as the tile line goes. The mast has to run from the structure, through the tiles, down to the middle of the screen, so the tiles cannot sit deeper than the length we can build. If yours sit more than [tiles] below the structure, call us and we will work the length out with you.'
  };

  /**
   * Token substitution for the copy above. Interpolated measurements are written
   * as [tiles] and [length] in the editable string, so an owner can move them,
   * reword around them, or delete one outright. A token we have no value for
   * renders as nothing rather than leaving a bracket on screen, and nothing here
   * can throw: the worst an edit can do is print a sentence with a number missing.
   */
  function fillTokens(str, map) {
    if (typeof str !== 'string') return '';
    var m = map || {};
    return str.replace(/\[(\/?[a-z]+)\]/g, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(m, key) ? String(m[key]) : '';
    });
  }

  function make(tag, cls, parent) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (parent) parent.appendChild(e);
    return e;
  }

  function px(v) { return Math.round(v) + 'px'; }

  function show(el, on) {
    el.style.display = on ? '' : 'none';
  }

  // Remove the closed interval [lo, hi] from a list of open segments, returning the
  // surviving pieces. Pure list arithmetic, so chooseBreakBand can run in Node.
  function subtractInterval(segs, lo, hi) {
    var out = [], i, s;
    for (i = 0; i < segs.length; i++) {
      s = segs[i];
      if (hi <= s.from || lo >= s.to) { out.push(s); continue; }   // no overlap, keep whole
      if (lo > s.from) out.push({ from: s.from, to: lo });          // pipe above the exclusion
      if (hi < s.to) out.push({ from: hi, to: s.to });              // pipe below it
    }
    return out.filter(function (q) { return q.to > q.from; });
  }

  // [RULE] ONE MAPPING, TWO DIRECTIONS, and they are EXACT inverses. Depth in
  // inches to px through mapY(), px back to inches through mapYInv(), over an ARRAY
  // of bands (revision 2: one break per long pipe). Each band collapses its own
  // run to a fixed GLYPH_PX gap. Module-level and pure so the round trip is unit
  // testable in Node; the mount's y()/yInv() delegate to these with the live ppi
  // and bands. With ZERO bands both reduce byte-for-byte to the linear form. The
  // bands are sorted by depth and never overlap. Do not add a second scale anywhere.
  function mapY(depthIn, ppi, bands) {
    var v = TOP_PAD_PX + depthIn * ppi;
    if (!bands || !bands.length) return v;
    for (var i = 0; i < bands.length; i++) {
      var a = bands[i].a, len = bands[i].b - a;
      if (depthIn >= bands[i].b) v -= (len * ppi - GLYPH_PX);          // band fully above depthIn
      else if (depthIn > a) { v -= ((depthIn - a) * ppi - (depthIn - a) / len * GLYPH_PX); break; }
      else break;                                                       // depthIn at or above this band
    }
    return v;
  }
  function mapYInv(pxV, ppi, bands) {
    if (!bands || !bands.length) return (pxV - TOP_PAD_PX) / ppi;
    var acc = 0;   // px removed by every band already fully above the query point
    for (var i = 0; i < bands.length; i++) {
      var a = bands[i].a, len = bands[i].b - a;
      var yA = TOP_PAD_PX + a * ppi - acc;      // px at the band's top (linear up to here)
      if (pxV <= yA) return (pxV - TOP_PAD_PX + acc) / ppi;
      var yB = yA + GLYPH_PX;                    // px at the band's bottom (the gap is GLYPH_PX)
      if (pxV < yB) return a + (pxV - yA) / GLYPH_PX * len;   // inside the gap
      acc += (len * ppi - GLYPH_PX);
    }
    return (pxV - TOP_PAD_PX + acc) / ppi;
  }

  // Owner ruling 2026-09-18, verbatim: "nothing should draw flush against the pipe
  // they should have the overlap". The half-height anchoring assumes a head's VESA
  // hole pattern sits at the cutout's vertical middle; on the 22HE family it sits
  // 0.62 in BELOW middle, so a VERTICAL head drawn with art can end with its top a
  // hair BELOW the pipe end (raw overlap < edgeOverlayIn, even negative), a visible
  // gap. Returns how far UP, in scene inches, to nudge the head CUTOUT so its top
  // sits exactly edgeOverlayIn above the pipe end; 0 when the floor does not apply
  // (no art, horizontal head, missing depths, or the raw overlap already clears the
  // floor), so every other head stays pixel-identical. Module-level and PURE so the
  // DOM path (layoutHead, px = nudge * ppi) and the band-clearance path
  // (headTopScene, inches) share ONE rule and it is unit testable. The nudge region
  // is the last edgeOverlayIn of the mast just above the pipe end and carries no
  // break band (headTopScene keeps bands clear of the nudged, shallower head top),
  // so that region maps linearly at ppi and nudge * ppi is exact.
  function headArtNudgeIn(hasArt, horizontal, headHeightIn, mountDepthIn, pipeBottomIn, edgeOverlayIn) {
    if (!hasArt || horizontal) return 0;
    if (mountDepthIn == null || pipeBottomIn == null) return 0;
    // Overlap of the head top over the pipe end: head top is mountDepth - h/2, the
    // pipe end is pipeBottom, overlap = pipeBottom - head top.
    var rawOverlapIn = headHeightIn / 2 - (mountDepthIn - pipeBottomIn);
    return rawOverlapIn < edgeOverlayIn ? (edgeOverlayIn - rawOverlapIn) : 0;
  }

  // The longest usable interval inside one fixed piece: the piece minus STUB_IN at
  // each end (so both cut ends and every coupler joint keep pipe on both sides)
  // minus every exclusion. Returns {from, to, len} or null.
  function pieceInterval(piece, excl) {
    var segs = [{ from: piece.start + STUB_IN, to: piece.end - STUB_IN }];
    segs = segs.filter(function (q) { return q.to > q.from; });
    for (var e = 0; e < excl.length; e++) segs = subtractInterval(segs, excl[e].from, excl[e].to);
    var best = null;
    for (var s = 0; s < segs.length; s++) {
      var len = segs[s].to - segs[s].from;
      if (!best || len > best.len) best = { from: segs[s].from, to: segs[s].to, len: len };
    }
    return best;
  }

  /**
   * Choose the runs of PLAIN pipe to cut, ONE PER FIXED PIECE (revision 2), as a
   * pure function so it is unit-testable in Node with no DOM. All values are SCENE
   * inches from the structure (the caller has mapped mast-spec positions onto the
   * drawn run and turned the tile line, the TV and the head into exclusions).
   *
   * [RULE, revision 4, owner 2026-09-06] CANDIDATES ARE EVERY FIXED PIECE PLUS A
   * TELESCOPING PIECE'S OUTER TUBE PLAIN BODY. The caller passes a telescoping
   * piece's plain span from telePlainSpans, which is the larger-diameter OUTER
   * tube's plain run only; the exposed smaller-diameter INNER sliding run is NEVER a
   * candidate (owner: "in the larger diameter 'outer' portion of the pipes I want
   * it", but not the smaller diameter part), because a cut through the exposed inner
   * strip is a cut through the very thing a customer reads as "adjustable". This
   * function does not know a piece's kind: it just receives intervals, so a tele
   * span arrives here already reduced to its cuttable outer body. Each interval
   * contributes at most ONE band (its longest usable interval), and the blank band
   * carries only the two angled lines (no dots, revision 3 12b stays withdrawn).
   *
   * GREEDY, LONGEST INTERVAL FIRST. A band is opened only while the remaining need
   * is at least MIN_BREAK_IN. Opening a band costs `glyphCostIn` extra inches of
   * the budget (the gap is pixels the budget must pay for), so that cost is added
   * to the remaining need as each band opens; the band takes min(remaining,
   * intervalLength) and is centred in its interval. Result is sorted by depth.
   *
   * @param {Object} inp {pieces, exclusions, needed, glyphCostIn}
   * @returns {Array<{a,b}>} bands sorted by depth (empty for no break)
   */
  function chooseBreakBands(inp) {
    if (!inp || !(num(inp.needed) > 0)) return [];
    var pieces = inp.pieces || [], excl = inp.exclusions || [];
    var glyphCostIn = num(inp.glyphCostIn) != null ? inp.glyphCostIn : 0;
    var cands = [], i;
    for (i = 0; i < pieces.length; i++) {
      var iv = pieceInterval(pieces[i], excl);
      if (iv && iv.len >= MIN_BREAK_IN) cands.push(iv);
    }
    cands.sort(function (a, b) { return b.len - a.len; });   // longest interval first
    var bands = [], remaining = inp.needed;
    for (i = 0; i < cands.length; i++) {
      if (remaining < MIN_BREAK_IN) break;
      remaining += glyphCostIn;                              // opening a band costs its glyph gap
      var take = Math.min(remaining, cands[i].len);
      if (take < MIN_BREAK_IN) continue;
      var a = cands[i].from + (cands[i].len - take) / 2;     // centre the band in its interval
      bands.push({ a: a, b: a + take });
      remaining -= take;
    }
    bands.sort(function (x, y) { return x.a - y.a; });        // by depth, for mapY/mapYInv
    return bands;
  }

  // Thin singular wrapper kept for callers and tests that want just the shallowest
  // band (or null). Revision 1's chooseBreakBand contract.
  function chooseBreakBand(inp) {
    var bands = chooseBreakBands(inp);
    return bands.length ? bands[0] : null;
  }

  /**
   * Mount a scene into rootEl.
   *
   * @param {HTMLElement} rootEl  a container that is NEVER rebuilt by innerHTML.
   * @param {Object} opts
   *        onDropChange {function(rawInches, phase)} phase is 'move' or 'commit'
   *        onTileChange {function(rawInches, phase)}
   *        spriteUrl    {string} passed straight to VMSMastArt
   *        copy         {Object} optional owner-edited at-limit sentences, keyed
   *                     exactly like COPY_DEFAULTS above. Any key that is missing,
   *                     not a string, or blank falls back to the shipped default,
   *                     so a partial map is safe and an absent one changes nothing.
   *        maxHeightPx  {number} optional UPPER BOUND on the vertical budget. The
   *                     budget itself comes from the --vmscfg-scene-h custom
   *                     property in the stylesheet, so the mobile height lives
   *                     with the mobile breakpoint; this only caps it. Neither
   *                     present means DEFAULT_HEIGHT_PX.
   * @returns {{update:function, destroy:function, root:HTMLElement}}
   */
  function mount(rootEl, opts) {
    opts = opts || {};

    if (!rootEl || !rootEl.appendChild) {
      // Degrade visibly in code, never throw at the caller: a missing container is
      // a wiring mistake, not a reason to take the configurator down with us.
      if (window.console) console.warn('vms-elevation-scene: no container to mount into');
      return { update: function () {}, destroy: function () {}, root: null };
    }

    var id = NS + '-' + (++uid);
    var model = {};
    var ppi = 0, stageW = 0, stageH = 0, lastW = -1;
    // The break bands in SCENE inches, an array of {a, b} sorted by depth (empty
    // for no break; revision 2 allows one per fixed pipe). Recomputed every
    // unlocked layout and FROZEN into lock for the length of a gesture, exactly as
    // ppi is: y()/yInv() read it, so a gesture never changes where the pipe is cut.
    var bands = [];
    var listeners = [];
    var mastCanvas = null, mastKey = null;
    var ro = null, rafId = 0, liveFlip = false;
    // The frozen scale. Null when nothing is being dragged. See the file header:
    // while this is set, NOTHING in the drawing changes size and the stage cannot
    // change the page layout.
    var lock = null;
    var budgetPx = 0, budgetW = -1;   // cached CSS height budget, keyed on the width
    var labelPool = [];

    // ---- DOM ---------------------------------------------------------------
    var scene = make('div', NS, rootEl);
    var stage = make('div', NS + '__stage', scene);

    var structure = make('div', NS + '__structure', stage);
    structure.setAttribute('aria-hidden', 'true');
    var plate = make('div', NS + '__plate', stage);
    plate.setAttribute('aria-hidden', 'true');
    // The drawing itself is decorative to a screen reader: the numbers it encodes
    // are all spoken by the two sliders' aria-valuetext, and the mast's real parts
    // and length are read out by the summary panel. So the art is aria-hidden and
    // nothing is lost. Do not label the canvas as well, or the same fact is spoken
    // twice with two different wordings.
    var mastSlot = make('div', NS + '__mast', stage);
    mastSlot.setAttribute('aria-hidden', 'true');
    var pipeFallback = make('div', NS + '__pipe', stage);
    pipeFallback.setAttribute('aria-hidden', 'true');
    // POOLS for the drag-time plain pipe when the frozen bands cut it: revision 2
    // splits the mast into n + 1 pipe pieces with a zigzag cut end each and draws n
    // SVG break glyphs in the gaps. pipeFallback is the FIRST piece; extra pieces
    // and the glyphs are created on demand here and reused, so a build with more
    // bands never re-allocates. They exist only while a gesture has the scale frozen
    // (or the sprite has failed) and mirror the canvas glyph so nothing jumps at
    // grab or release. Dawn hides empty divs, so extra pieces reuse the
    // .vmscfg-scene__pipe class (already in the CSS display list) and each SVG
    // carries its own explicit display rule.
    var SVGNS = 'http://www.w3.org/2000/svg';
    var pipePool = [pipeFallback];   // index 0 is the first piece
    var glyphPool = [];              // one SVG per gap, grown on demand
    function pipePieceAt(i) {
      while (pipePool.length <= i) {
        var p = make('div', NS + '__pipe', stage);
        p.setAttribute('aria-hidden', 'true');
        p.style.display = 'none';
        pipePool.push(p);
      }
      return pipePool[i];
    }
    function glyphAt(i) {
      while (glyphPool.length <= i) {
        var g = document.createElementNS(SVGNS, 'svg');
        g.setAttribute('class', NS + '__break');
        g.setAttribute('aria-hidden', 'true');
        g.style.display = 'none';
        stage.appendChild(g);
        glyphPool.push(g);
      }
      return glyphPool[i];
    }

    // ORTHO CUTOUT for the ceiling plate. An <img>, NOT a canvas, and that is the
    // whole reason the art can live in the drag panel at all: during a drag the
    // plate does not move (it is anchored to depth 0) and its picture never
    // changes, so there is nothing to redraw per frame. Only the pipe changes
    // shape, which vms-mast-art already handles. The 2026-07-26 objection to art
    // in this scene assumed recompositing all three parts every pointermove; that
    // premise was wrong and D2 reverses it on those grounds.
    //
    // AFTER the mast elements in DOM order so it paints OVER the pipe with no
    // z-index: the collar has to swallow the pipe's top end.
    //
    // An <img> is not a div, so Dawn's blanket `div:empty { display: none }` does
    // not reach it and it needs no entry in the override list. The schematic
    // .vmscfg-scene__plate it replaces still does, and still has one.
    var plateArt = make('img', NS + '__partart', stage);
    plateArt.setAttribute('aria-hidden', 'true');
    plateArt.alt = '';
    plateArt.style.display = 'none';

    // Head wrapper: transparent, carries the pointer and key handling. The TV fill
    // and the head block are SIBLINGS inside it, so neither dims the other.
    var headEl = make('div', NS + '__handle ' + NS + '__head', stage);
    // THREE elements, not one, and the split is load bearing. The parent carries
    // the RULES, the child carries the FILL and the fade mask that only the fill
    // may have, and the caption names the band for a customer who can see neither
    // end of it. See the crop section of the file header.
    //
    // [WARN] SPANS, not divs. Dawn's base.css hides every empty div on the page
    // (see the display list in vms-configurator.css) and the fill is empty by
    // design. Spans are not in Dawn's selector list, so they need no guard.
    //
    // aria-hidden on the whole TV: it is art, the head slider already speaks its
    // position and the summary panel already states the screen size, so a caption
    // in the tree would be a third wording of one fact. Nothing here is focusable,
    // so hiding it cannot orphan a control.
    var tvEl = make('div', NS + '__tv', headEl);
    tvEl.setAttribute('aria-hidden', 'true');
    var tvFill = make('span', NS + '__tvfill', tvEl);
    var tvName = make('span', NS + '__tvname', tvEl);
    // ORTHO CUTOUT for the mounting head. Sibling of the TV and drawn after it,
    // so the head sits in front of the see-through screen exactly as the
    // schematic block did.
    //
    // [THE ANCHORING IS ALMOST FREE] this element is centred on the head wrapper,
    // and the wrapper's own centre is already pinned to the mount depth by
    // `transform: translate(-50%, -50%)`. Under the owner's half-height rule a
    // head's hole pattern is its image's vertical middle: image top = pipeBottom -
    // overlap = (drop - offset) - (h/2 - offset) = drop - h/2, so the image's centre
    // lands on the drop by construction. That shortcut is WRONG on a head whose VESA
    // centre is off mid-height (22HE001, 22HE021 sit 0.62 in below middle), where
    // h/2 - offset drops below 0.350 or goes negative and the head would draw flush
    // or a hair short. Owner ruling 2026-09-18, verbatim: "nothing should draw flush
    // against the pipe they should have the overlap". So layoutHead nudges the
    // cutout UP by headArtNudgeIn for that case; this centring is the base the nudge
    // is composed onto.
    var headArt = make('img', NS + '__partart ' + NS + '__headart', headEl);
    headArt.setAttribute('aria-hidden', 'true');
    headArt.alt = '';
    headArt.style.display = 'none';

    var headBlock = make('div', NS + '__block', headEl);
    var headGrip = gripInto(headEl);

    // Tile line LAST, so its full width hit area sits above the head wrapper. The
    // dashed line is thin and a customer aims at it; the head is a large object and
    // losing a 24 px strip of it at the tile line costs nothing.
    var tileEl = make('div', NS + '__handle ' + NS + '__tile', stage);
    // Name for the dashed line, first flex item so it sits at the LEFT end.
    // [WARN] It MUST be a span, not a div: Dawn's base.css carries
    // `div:empty { display: none }` and span is not in that selector list, which
    // is exactly why tileRule below is a span too. An owner who blanks the label
    // setting leaves this empty, and an empty div would vanish and take its flex
    // slot with it.
    var tileLabel = make('span', NS + '__tilelabel', tileEl);
    // aria-hidden: the tile handle's own aria-label already reads "Drop ceiling,
    // from the structure down to the tiles, in inches" and its aria-valuetext
    // narrates the number, so an exposed label here would be a second competing
    // voice describing the one thing the slider already names.
    tileLabel.setAttribute('aria-hidden', 'true');
    tileLabel.textContent = copy('tileLabel');
    // The last SHOWN answer, remembered so a gesture can reuse it rather than
    // re-deciding on a picture that is changing under the hand. It is the FINAL
    // state, not just the width test's opinion, because the left gutter gets a
    // second say over it after the labels are placed. See layoutTile and the
    // occupancy downgrade in layoutLabels.
    var tileLabelShown = true;
    var tileRule = make('span', NS + '__rule', tileEl);
    var tileGrip = gripInto(tileEl);

    // The part labels go in LAST so they paint over the TV and the two handles.
    //
    // [WARN] pointer-events MUST stay off this layer, in the CSS. It covers the
    // whole stage, so anything that accepts a pointer here shoots holes through
    // the head's hit area, which is the largest and most obvious drag target in
    // the drawing.
    //
    // aria-hidden for the same reason the rest of the art is: the two sliders
    // already speak every number this drawing encodes, and the summary panel's
    // parts table already lists the real parts with their SKUs and lengths. These
    // labels are visual identification of what is already narrated, so putting
    // them in the accessibility tree would start a second, competing description
    // of one build.
    var labelLayer = make('div', NS + '__labels', stage);
    labelLayer.setAttribute('aria-hidden', 'true');

    var hint = make('p', NS + '__hint', scene);
    hint.id = id + '-hint';
    hint.textContent = 'Drag the mount or the tile line to set the height. Arrow keys work too, once a handle is focused.';

    var reason = make('p', NS + '__reason', scene);
    var live = make('span', NS + '__live', scene);
    live.setAttribute('aria-live', 'polite');

    function gripInto(parent) {
      var g = make('span', NS + '__grip', parent);
      g.setAttribute('aria-hidden', 'true');   // the handle's aria-label already says it
      make('span', NS + '__bars', g);
      var t = make('span', NS + '__griptxt', g);
      t.textContent = 'Drag';
      var v = make('span', NS + '__val', g);
      // txt is returned so the tile chip can rename its word to the drop-ceiling
      // label at narrow widths (see layoutTile). The head never touches it.
      return { el: g, val: v, txt: t };
    }

    // ---- listener registry -------------------------------------------------
    function bind(target, type, fn, o) {
      target.addEventListener(type, fn, o);
      listeners.push([target, type, fn, o]);
    }

    // ---- the frozen scale --------------------------------------------------
    // Lock the values ALREADY IN USE, never freshly computed ones, or the picture
    // jumps at the exact moment the customer grabs it.
    function lockScale() {
      if (lock) return;            // a second handle joins the freeze already running
      if (!(ppi > 0) || !(stageH > 0)) return;
      // The bands freeze with the scale: y()/yInv() must not re-cut the pipe mid
      // gesture, or the handle would drift as the mapping changed under the hand.
      lock = { ppi: ppi, stageH: stageH, bands: bands };
      layout();                    // swap in the plain pipe and drop the labels
    }

    // [WARN] EVERY path that can end a gesture calls this, and it is deliberately
    // idempotent and self-guarding: pointerup, pointercancel, keyup, blur, the
    // lost-keyup idle timer and destroy() all land here. A lock that outlives its
    // gesture leaves the customer looking at a plain pipe at a stale scale
    // forever, so the cheap defence is that unlocking is safe to call twice and
    // refuses only while some OTHER handle still has the picture.
    function unlockScale() {
      if (!lock) return;
      if (head.drag || tile.drag || head.keyRun || tile.keyRun) return;
      lock = null;
      // rAF, not a direct layout(): the caller's own commit is about to arrive
      // with a freshly solved model and lay the scene out synchronously. This is
      // only the guarantee that the scale recovers even if it never does.
      schedule();
    }

    // ---- handles -----------------------------------------------------------
    function makeHandle(kind, el, grip, emit, label) {
      var h = {
        kind: kind, el: el, grip: grip, emit: emit,
        value: null, min: 0, max: 0, maxKnown: false,
        atMin: false, atMax: false,
        pending: null, drag: null, hold: 0, keyHeld: false, lastRaw: null,
        keyRun: false, keyIdle: 0
      };

      el.setAttribute('role', 'slider');
      el.setAttribute('tabindex', '0');
      el.setAttribute('aria-orientation', 'vertical');
      el.setAttribute('aria-label', label);
      el.setAttribute('aria-describedby', hint.id);

      function clearHold() {
        if (h.hold) { window.clearTimeout(h.hold); h.hold = 0; }
      }

      function startDrag(p) {
        clearHold();
        h.pending = null;
        h.drag = p;
        h.lastRaw = p.startValue;
        try { el.setPointerCapture(p.id); } catch (err) { /* capture is a nicety, not a requirement */ }
        el.classList.add(NS + '__handle--grabbing');
        // Scoped to THIS handle and only for the life of THIS gesture. See the
        // scroll trap warning in the file header.
        el.style.touchAction = 'none';
        // AFTER h.drag is set, so unlockScale can see that this gesture owns it.
        lockScale();
      }

      // A run of arrow presses is ONE gesture, so it freezes the scale exactly
      // like a drag does. Home and End do not: they commit on the spot, so a
      // freeze would be locked and cleared inside one event.
      function startKeyRun() {
        if (h.keyIdle) { window.clearTimeout(h.keyIdle); h.keyIdle = 0; }
        h.keyRun = true;
        lockScale();
        h.keyIdle = window.setTimeout(function () { endKeyRun(); }, KEY_IDLE_MS);
      }

      // [WARN] Ends the FREEZE only. It deliberately emits nothing: keyup owns
      // the commit, and blur has always abandoned an uncommitted key run without
      // one. Adding an emit here would change that contract from a file that is
      // only meant to be changing the scale.
      function endKeyRun() {
        if (h.keyIdle) { window.clearTimeout(h.keyIdle); h.keyIdle = 0; }
        if (!h.keyRun) return;
        h.keyRun = false;
        unlockScale();
      }

      function endDrag() {
        if (!h.drag) return;
        try { el.releasePointerCapture(h.drag.id); } catch (err) { /* already gone */ }
        h.drag = null;
        el.classList.remove(NS + '__handle--grabbing');
        el.style.touchAction = '';
        unlockScale();     // h.drag is already null, so this gesture no longer holds it
      }

      function down(e) {
        if (e.button != null && e.button > 0) return;      // right or middle button
        if (h.drag) return;
        var base = num(h.value) != null ? h.value : 0;
        try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
        var p = { id: e.pointerId, x: e.clientX, y: e.clientY, startValue: base };
        if (e.pointerType === 'touch') {
          // No preventDefault yet. The page must still be free to scroll until the
          // hold test says this was meant as a drag.
          h.pending = p;
          h.hold = window.setTimeout(function () {
            if (h.pending === p) startDrag(p);
          }, TOUCH_HOLD_MS);
          return;
        }
        e.preventDefault();     // mouse and pen only: suppresses the text selection drag
        startDrag(p);
      }

      function move(e) {
        if (h.pending && !h.drag) {
          if (h.pending.id !== e.pointerId) return;
          var travel = Math.abs(e.clientY - h.pending.y) + Math.abs(e.clientX - h.pending.x);
          if (travel > TOUCH_SLOP_PX) { clearHold(); h.pending = null; }   // it is a scroll, let it go
          return;
        }
        if (!h.drag || h.drag.id !== e.pointerId) return;
        if (!(ppi > 0)) return;
        // RAW inches. No clamp, no quantise, no rounding. Absolute from the value
        // captured at pointerdown, so a clamped value cannot creep. Routed through
        // y()/yInv() so a frozen band maps the pointer travel correctly: with no
        // band this IS startValue + (clientY - startY) / ppi, byte for byte.
        var raw = yInv(y(h.drag.startValue) + (e.clientY - h.drag.y));
        h.lastRaw = raw;
        h.emit(raw, 'move');
      }

      function up(e) {
        if (h.pending && h.pending.id === e.pointerId) { clearHold(); h.pending = null; }
        if (!h.drag || h.drag.id !== e.pointerId) return;
        var raw = num(h.lastRaw) != null ? h.lastRaw : h.value;
        endDrag();
        h.emit(raw, 'commit');
        announce(h);
      }

      function cancel() {
        clearHold();
        h.pending = null;
        if (!h.drag) return;
        var raw = num(h.lastRaw) != null ? h.lastRaw : h.value;
        endDrag();
        h.emit(raw, 'commit');   // a cancelled gesture still has to settle somewhere
      }

      function touchmove(e) {
        // Non-passive on purpose, and it only ever fires preventDefault once the
        // gesture has been claimed. This is the whole scroll trap defence.
        if (h.drag) e.preventDefault();
      }

      function keydown(e) {
        if (e.altKey || e.ctrlKey || e.metaKey) return;
        var step = num(model.step) != null && model.step > 0 ? model.step : DEFAULT_STEP;
        var fine = e.shiftKey ? step * COARSE_MULT : step;
        var coarse = step * COARSE_MULT;
        var base = num(h.value) != null ? h.value : 0;
        var raw = null, commit = false;
        switch (e.key) {
          case 'ArrowDown': case 'Down': case 'ArrowRight': case 'Right':
            raw = base + fine; break;
          case 'ArrowUp': case 'Up': case 'ArrowLeft': case 'Left':
            raw = base - fine; break;
          case 'PageDown': raw = base + coarse; break;
          case 'PageUp': raw = base - coarse; break;
          case 'Home': raw = h.min; commit = true; break;
          case 'End': raw = h.maxKnown ? h.max : base; commit = true; break;
          default: return;    // Tab, Space, Enter and everything else are left alone
        }
        e.preventDefault();
        h.lastRaw = raw;
        h.keyHeld = !commit;
        // Freeze BEFORE emitting, so the layout the emit triggers is already the
        // frozen one and the picture never rescales between two arrow presses.
        if (commit) endKeyRun(); else startKeyRun();
        h.emit(raw, commit ? 'commit' : 'move');
        if (commit) announce(h);
      }

      function keyup(e) {
        if (!h.keyHeld) return;
        if (e.key === 'Shift') return;
        h.keyHeld = false;
        endKeyRun();
        h.emit(num(h.lastRaw) != null ? h.lastRaw : h.value, 'commit');
        announce(h);
      }

      function blur() {
        h.keyHeld = false;
        endKeyRun();
        clearHold();
        h.pending = null;
      }

      bind(el, 'pointerdown', down);
      bind(el, 'pointermove', move);
      bind(el, 'pointerup', up);
      bind(el, 'pointercancel', cancel);
      bind(el, 'touchmove', touchmove, { passive: false });
      bind(el, 'keydown', keydown);
      bind(el, 'keyup', keyup);
      bind(el, 'blur', blur);
      // Deliberately NO wheel listener. A wheel over a drawing silently creeps a
      // value the customer already set, which is one of the defects this build
      // exists to avoid.

      // Silent teardown for destroy(). Deliberately NOT cancel(): a caller that is
      // pulling the scene down does not want a callback fired at it on the way out.
      // keyRun goes down BEFORE endDrag, or the unlock inside endDrag sees this
      // handle still holding the scale and declines.
      h._stop = function () {
        clearHold();
        h.pending = null;
        h.keyHeld = false;
        endKeyRun();
        endDrag();
        unlockScale();
      };
      return h;
    }

    function emitDrop(raw, phase) {
      if (typeof opts.onDropChange === 'function') opts.onDropChange(raw, phase);
    }
    function emitTile(raw, phase) {
      if (typeof opts.onTileChange === 'function') opts.onTileChange(raw, phase);
    }

    var head = makeHandle('drop', headEl, headGrip, emitDrop,
      'Length, from the structure down to the middle of the screen, in inches');
    var tile = makeHandle('tile', tileEl, tileGrip, emitTile,
      'Drop ceiling, from the structure down to the tiles, in inches');

    // ---- limits ------------------------------------------------------------
    // THE ENFORCED STOPS, HANDED DOWN BY THE CALLER. This module no longer works
    // out its own, and that is the whole fix.
    //
    // [WARN] DO NOT GO BACK TO DERIVING THESE FROM model.minDrop. That field is
    // the RAW GEOMETRIC minimum, 35.784143387139565 in for a 70 in screen under
    // 18 in of tile depth. It is NOT a number the customer can ever reach: the
    // caller clamps every committed value to that floor rounded UP onto the
    // number field's 0.5 in grid, so the stop is 36. The difference is up to a
    // whole step, which is 25 times LIMIT_EPS, so atMin was never true at the
    // stop the drag and the keyboard actually reach. Measured on the dev theme
    // 2026-07-26: typing 20 into the number field explained itself, because that
    // path asks the caller, while dragging up into the same stop, and ArrowUp,
    // and Home, all sat there silently with no at-limit class, no reason line
    // and nothing announced. A silent hard stop is one of the named defects in
    // section 7.2 of SPEC-drag-elevation-2026-07-26.md. Same story on the tile
    // handle: the enforced cap is the field's own max and this module was
    // advertising a geometric one, so aria-valuemax said 69.72 while the value
    // was being clamped at 60. (That 60 was itself replaced later the same day:
    // the tile cap is now DERIVED per TV size by the caller, so tileMaxEnforced
    // moves with the screen. Nothing in here changes; it still just uses what it
    // is handed, which is the whole point of the fix.)
    //
    // minDrop and minDropReason stay in the model and stay load-bearing:
    // minDropReason still chooses the WORDING, which is the part this module
    // owns. The NUMBERS come from dropMinEnforced, dropMaxEnforced,
    // tileMinEnforced and tileMaxEnforced and from nowhere else.
    //
    // The fallbacks below only run for a caller that sends none of that. A
    // standalone module cannot position a handle against undefined, but be clear
    // about what they are: advisory arithmetic that is allowed to disagree with
    // whatever the caller is really enforcing, i.e. a silent stop again. Send
    // the enforced numbers.
    function limits(h) {
      if (h.kind === 'drop') {
        var lo = num(model.dropMinEnforced);
        var hi = num(model.dropMaxEnforced);
        if (lo == null) lo = num(model.minDrop) != null ? model.minDrop : 0;
        h.min = lo;
        h.maxKnown = hi != null;
        h.max = hi != null ? hi : (num(model.sceneDepth) != null ? model.sceneDepth : FALLBACK_DEPTH_IN);
        return;
      }
      var tlo = num(model.tileMinEnforced);
      var thi = num(model.tileMaxEnforced);
      if (tlo != null && thi != null) {
        h.min = tlo;
        h.max = thi;
        h.maxKnown = true;
        return;
      }
      var mountD = num(model.mountDepth);
      // THE CABINET, since the bezel allowance of 2026-07-28. On the wired path
      // tvHeight is now that same cabinet height (the owner's drawing correction of
      // 2026-07-28 made the drawn rectangle the whole TV), so the two branches agree;
      // the order is kept because a standalone caller can still send a screen-only
      // tvHeight, and taking the cabinet first is what keeps this fallback from
      // running half an inch looser than the cap the configurator really enforces.
      // The 1 in allowance is deliberately NOT duplicated into this file; it is read
      // off the model.
      //
      // [WARN] AND MIN_CLEARANCE ON THE NEXT LINE IS EXACTLY THE DUPLICATION THAT
      // SENTENCE WARNS ABOUT. It is a second copy of VMSElevation.MIN_CLEARANCE,
      // declared at the top of this file, and the formula below is its only use.
      // Pre-existing, flagged by adversarial review 2026-07-28, left standing on
      // purpose: this whole branch is dead on the wired path (the configurator
      // always sends tileMinEnforced and tileMaxEnforced), and threading a real
      // constant in would mean changing the module's public shape for a fallback
      // nothing reaches. Move it to the model if this branch ever goes live.
      //
      // Note also that this does NOT literally mirror VMSElevation.maxTileDepth:
      // that solves against the drop MAXIMUM, this uses the current drop. It is the
      // same rule against a different bound, which is another reason it is only ever
      // advisory.
      var tvH = num(model.tvCabinetHeight) != null ? num(model.tvCabinetHeight) : num(model.tvHeight);
      var cap;
      if (mountD != null && tvH != null) cap = (!model.horizontal && num(model.tvCabinetTop) != null) ? model.tvCabinetTop - MIN_CLEARANCE : mountD - tvH / 2 - MIN_CLEARANCE;
      else if (mountD != null) cap = mountD;
      else cap = num(model.sceneDepth) != null ? model.sceneDepth : FALLBACK_DEPTH_IN;
      if (cap < 0) cap = 0;
      h.min = tlo != null ? tlo : 0;
      h.max = cap;
      h.maxKnown = mountD != null;
    }

    // ---- copy at the stops -------------------------------------------------
    // Every wording choice below is governed by website-copy-voice.md: plain, first
    // sentence answers the question, no dashes, no hype.
    // `kind` scopes the at-limit sentences to ONE handle: 'drop', 'tile', or
    // omitted for the visible line under the drawing, which explains whatever is
    // currently at a stop.
    //
    // The announcement passes the handle being moved, because both handles can
    // legitimately be at a stop at the same time and reading out the other one's
    // sentence is noise at exactly the moment the customer wants an answer about
    // the thing under their finger. Measured before the enforced-limits fix:
    // holding the drop at its floor announced "This is as high as the mount can
    // go. The top of the screen has to clear the tiles by at least 1/8 inch. The
    // tiles have to stay above the top of the screen." Three sentences, the last
    // two being one constraint stated from both sides.
    /**
     * One sentence, owner-edited if the caller handed one over and shipped-default
     * otherwise. See COPY_DEFAULTS at the top of the file for why this lives on
     * one function rather than at the call sites.
     */
    function copy(key, map) {
      var v = opts.copy ? opts.copy[key] : null;
      if (typeof v !== 'string' || v.trim() === '') v = COPY_DEFAULTS[key];
      return fillTokens(v, map);
    }

    function reasonLines(kind) {
      var out = [];

      // [WARN] GATED ON THE FLAG, NEVER ON reason === 'ceilingNoDepth'. This is the
      // DEFAULT state of the feature: a drop ceiling declared and no depth typed
      // yet. Since 2026-07-27 minDrop returns the MAXIMUM of every applicable
      // constraint, and the ceilingNoDepth branch contributes a floor of ZERO, so
      // it loses the reason slot to any parts or screen floor that binds. On the
      // commonest path of all, drop ceiling picked and TV size entered, the reason
      // is now screenClearsPlate and this ask would have vanished at exactly the
      // moment it matters most. needsTileDepth states the outstanding question
      // separately and is true whenever a drop ceiling has no usable depth,
      // whatever is binding.
      //
      // Its zero minimum is not proof that nothing is constraining the build, and
      // it must never be worded as "no drop ceiling". It is a request for the
      // distance from the structure down to the tiles.
      if (model.hasDropCeiling && model.needsTileDepth) {
        out.push(copy('needTiles'));
      }

      // [WARN] ADD A BRANCH HERE FOR EVERY REASON CODE minDrop GAINS. The full set
      // as of 2026-07-27 is none, shortestMast, screenClearsPlate, ceilingNoDepth,
      // belowTiles and tvClearsTiles; 2026-09-22 added headClearsTiles and
      // headClearsStructure for the horizontal heads. The else is the PARTS sentence, so a code
      // that is not about the parts and falls into it explains the stop by
      // pointing at the wrong thing, which is the silent-hard-stop defect of
      // section 7.2 with extra confidence. ceilingNoDepth cannot reach here: it
      // contributes a floor of zero, so head.atMin against a zero floor is the
      // "more than zero" case, not a stop worth a sentence.
      if (kind !== 'tile') {
        if (head.atMin) {
          if (model.minDropReason === 'tvClearsTiles') {
            // "TV", not "screen", since 2026-07-28: these floors clear the physical
            // cabinet, bezel included, so saying screen would understate what we
            // are actually holding clear. The reason CODES still say screen and are
            // left alone on purpose; they are API strings, not copy.
            out.push(copy('highTvTiles'));
          } else if (model.minDropReason === 'belowTiles') {
            out.push(copy('highBelowTiles'));
          } else if (model.minDropReason === 'screenClearsPlate') {
            // Word for word what vms-configurator.js says on its own surface for
            // this code. Two surfaces explaining one stop must not disagree.
            // [WARN] THE CODE NAME IS HISTORICAL. Since the owner ruling of
            // 2026-07-28 (evening) this stop is the TV clearing the CEILING, not
            // the ceiling plate. The reason string and the `highPlate` key are
            // deliberately unchanged: they are a cross-file API and the setting id
            // derived from them holds the owner's live copy edits.
            out.push(copy('highPlate'));
          } else if (model.minDropReason === 'headClearsTiles') {
            // [2026-09-22] A horizontal (face-down) head: its own top, not the TV,
            // clears the tiles. Same sentence as heldBecause() on the typed path.
            out.push(copy('highHeadTiles', { tiles: inchesText(num(model.tileDepth)) }));
          } else if (model.minDropReason === 'headClearsStructure') {
            out.push(copy('highHeadStructure'));
          } else {
            // shortestMast lands here, and the sentence is right for it: the floor
            // genuinely is the shortest mast the picked parts can build.
            out.push(copy('highParts'));
          }
        } else if (head.atMax) {
          out.push(copy('lowParts'));
        }
      }

      if (kind !== 'drop' && model.hasDropCeiling) {
        if (tile.atMin) {
          out.push(copy('tileMin'));
        } else if (tile.atMax && tile.maxKnown) {
          // The cap here is the tile field's own maximum, not the top of the
          // screen: the caller clamps the tile depth to that field and nothing
          // else, and the screen is protected by moving the DROP floor instead.
          // Wording it as "the tiles have to stay above the top of the screen"
          // described a limit nobody enforces.
          //
          // [WARN] REWRITTEN 2026-07-27, AND THE OLD SENTENCE IS A LESSON WORTH
          // KEEPING. It read "If your tiles sit more than 60 inches below the
          // structure, call us": it printed a cap nobody had ever chosen and
          // presented it as a product boundary. Real drop ceilings hang further
          // than 60 in below the structure all the time (warehouses, gyms,
          // big-box retail, atriums), so it turned an invented number into a
          // phone call. The cap is now DERIVED by the caller and the sentence has
          // to say the REAL reason: past this point there is no drop left in the
          // tool's range that still hangs the screen below the tiles. Print
          // tile.max, never a literal, because the number moves with the screen
          // size. The call-us line stays, because a plenum that deep genuinely is
          // something we work out by hand.
          var tcap = inchesText(tile.max);
          var dcap = inchesText(num(model.dropMaxEnforced));
          if (model.horizontal && model.minDropReason === 'headClearsTiles') {
            // [2026-09-22 round 3] Face-down head with a head-top rule: the cap is
            // the head clearing the tiles, not the TV. Vertical heads never reach it.
            out.push(copy('tileMaxHead', { tiles: tcap, length: dcap }));
          } else if (num(model.tvHeight) != null && dcap) {
            out.push(copy('tileMaxTv', { tiles: tcap, length: dcap }));
          } else {
            // No screen height known, so the cap is the plain outer bound and the
            // sentence must not claim a screen-based reason it cannot support.
            // It also must not name a drop maximum: with no dropMaxEnforced in the
            // model there is nothing to name, and quoting the tile cap as the drop
            // cap would be true only by coincidence on the wired path.
            out.push(copy('tileMaxPlain', { tiles: tcap }));
          }
        }
      }

      return out;
    }

    function announce(h) {
      var lines = [valueText(h)].concat(reasonLines(h.kind));
      // A live region only speaks when its text CHANGES, and holding a stop
      // produces the same sentence twice. The alternating trailing space is the
      // cheap, standard way to make the second one land.
      liveFlip = !liveFlip;
      live.textContent = lines.join(' ') + (liveFlip ? ' ' : '');
    }

    // [NOTE 2026-11-01] the value text names the same point as the handle name (placed TV: the center of its mounting holes)
    function valueText(h) {
      if (num(h.value) == null) return h.kind === 'drop' ? 'No length set yet.' : 'No tile depth set yet.';
      return h.kind === 'drop'
        ? inchesText(h.value) + ' from the structure to the ' + (model && model.horizontal ? 'back of the screen' : model && model.tvPlacement && model.tvPlacement.placed === true ? 'center of the TV\'s mounting holes' : 'middle of the screen') + '.'
        : inchesText(h.value) + ' from the structure down to the tiles.';
    }

    // ---- layout ------------------------------------------------------------
    // The drawn mapping and its inverse, with the live ppi and frozen band. This is
    // what lets the drag path emit yInv(y(startValue) + dy) and stay byte-for-byte
    // today's startValue + dy/ppi whenever no band exists. See mapY/mapYInv and the
    // file header's warning that the emitted value and the drawn position invert.
    function y(depthIn) { return mapY(depthIn, ppi, bands); }
    function yInv(pxV) { return mapYInv(pxV, ppi, bands); }

    /**
     * The vertical budget in px. CSS states it in --vmscfg-scene-h so the mobile
     * height sits next to the mobile breakpoint in the stylesheet; opts.maxHeightPx
     * is an UPPER BOUND on that, which keeps every existing call site meaningful.
     *
     * [WARN] getComputedStyle forces a style recalculation, and layout() runs on
     * every pointermove. The read is cached against the measured width, refreshed
     * on every update(), and skipped completely while the scale is locked, which
     * is exactly the drag path.
     */
    function heightBudget() {
      if (budgetW !== stageW) {
        budgetW = stageW;
        budgetPx = cssHeightPx();
      }
      var cap = num(opts.maxHeightPx);
      if (cap != null && cap > 0 && budgetPx > cap) return cap;
      return budgetPx;
    }

    function cssHeightPx() {
      var v = 0;
      try {
        v = parseFloat(window.getComputedStyle(scene).getPropertyValue(HEIGHT_VAR));
      } catch (err) { v = 0; }
      if (!(v > 0)) return DEFAULT_HEIGHT_PX;   // no stylesheet, or not a px value
      return Math.max(MIN_HEIGHT_PX, Math.min(v, MAX_HEIGHT_PX));
    }

    // Where the drawn mast starts (ceiling end) and the scale between mast-spec
    // inches and scene inches. Mirrors layoutMast: the pipe is drawn to the
    // DISTANCE between the plate and the head (fallbackLen) when that is within the
    // stretch tolerance of the parts-list length, so a piece's scene position must
    // ride the same scale or the band would land on the wrong pipe.
    function mastDrawGeom() {
      var spec = model.mastSpec, art = window.VMSMastArt;
      if (!art || !spec || !spec.length) return null;
      var specLen = art.totalInches(spec);
      if (!(specLen > 0)) return null;
      var pb = num(model.plateBottom);
      var top = num(model.pipeTop);
      if (top == null) top = pb != null ? pb : 0;
      var pipeBottom = num(model.pipeBottom);
      var fallbackLen = pipeBottom != null ? pipeBottom - top : null;
      var drawLen = specLen;
      if (fallbackLen != null && fallbackLen > 0 &&
          Math.abs(fallbackLen - specLen) <= MAST_STRETCH_MAX_IN) drawLen = fallbackLen;
      return { spec: spec, top: top, specLen: specLen, drawLen: drawLen,
               scale: drawLen / specLen };
    }

    // The shallowest (smallest-depth) top edge of the DRAWN head, in scene inches,
    // so the band can be kept clear of it the way it is of the TV.
    function headTopScene() {
      var mountD = num(model.mountDepth);
      if (mountD == null) return null;
      var art = partArt();
      var hIn = art ? artIn(art.head.naturalHeight) : HEAD_H_IN;
      // Horizontal (face-down) head hangs its art ABOVE the drop; vertical head is
      // centred on it, then floored UP by headArtNudgeIn (the module-level twin
      // layoutHead uses) so its top never sits less than ART_EDGE_OVERLAY_IN above
      // the pipe end (owner 2026-09-18). Same helper, so the band stays clear of the
      // DRAWN top.
      var nudge = headArtNudgeIn(!!art, !!model.horizontal, hIn, mountD, num(model.pipeBottom), ART_EDGE_OVERLAY_IN);
      return model.horizontal ? (mountD - hIn) : (mountD - hIn / 2 - nudge);
    }

    // Decide the break bands for the CURRENT (unlocked) model, in scene inches, or
    // an empty array. Revision 2: one band per fixed pipe, greedy longest first.
    // Revision 4 (owner 2026-09-06): a telescoping piece ALSO contributes one
    // candidate, its OUTER tube's plain body (telePlainSpans), never the exposed
    // inner sliding run. Gate on the unbroken ppi0, then remove enough across all
    // candidates to reach the target scale, each band charging its own glyph gap.
    function computeBands(depthIn, budget, byWidth, ppi0) {
      if (ppi0 >= BREAK_PPI_MIN) return [];   // legible unbroken, no break
      var g = mastDrawGeom();
      if (!g) return [];

      // Candidate intervals mapped onto the drawn run. A FIXED piece is one interval
      // (its whole span); a TELE piece contributes its plain span (rev 4: the outer
      // tube body only, end features and the exposed inner run already excluded).
      // chooseBreakBands applies the stubs and the exclusions to each. A tele piece's
      // length still advances the cursor so later pieces sit at their true positions.
      var art = window.VMSMastArt;
      var pieces = [], cursor = 0, i, j, len, spans;
      for (i = 0; i < g.spec.length; i++) {
        len = pieceInches(g.spec[i]);
        if (g.spec[i].kind === 'tele' && art.telePlainSpans) {
          spans = art.telePlainSpans(g.spec[i].collapsed, g.spec[i].extended, g.spec[i].current);
          for (j = 0; j < spans.length; j++) {
            pieces.push({ start: g.top + (cursor + spans[j].from) * g.scale,
                          end: g.top + (cursor + spans[j].to) * g.scale });
          }
        } else if (g.spec[i].kind !== 'tele') {
          pieces.push({ start: g.top + cursor * g.scale, end: g.top + (cursor + len) * g.scale });
        }
        cursor += len;
        if (i < g.spec.length - 1) cursor += (num(art.COUPLER_NET_ADD) != null
          ? art.COUPLER_NET_ADD : 1.5);
      }
      if (!pieces.length) return [];

      // Exclusions in scene inches: the tile line, and everything from the TV top
      // and the head top downward (a band must end clear ABOVE them).
      var excl = [];
      if (model.hasDropCeiling && num(model.tileDepth) != null) {
        excl.push({ from: model.tileDepth - TILE_CLEAR_IN, to: model.tileDepth + TILE_CLEAR_IN });
      }
      var mountD = num(model.mountDepth), tvH = num(model.tvHeight), tvW = num(model.tvWidth);
      if (mountD != null && tvH != null && tvW != null && tvH > 0 && tvW > 0) {
        excl.push({ from: (!model.horizontal && num(model.tvTop) != null ? model.tvTop : mountD - tvH / 2) - TV_CLEAR_IN, to: Infinity });
      }
      var ht = headTopScene();
      if (ht != null) excl.push({ from: ht - TV_CLEAR_IN, to: Infinity });

      // How much to remove to reach the target scale, computed with NO glyph in the
      // budget (n = 0): the chooser charges each glyph's cost as it opens a band.
      var targetPpi = Math.min(BREAK_PPI_TARGET, byWidth, MAX_PPI);
      var needed = (depthIn + BOTTOM_PAD_IN) - (budget - TOP_PAD_PX) / targetPpi;
      return chooseBreakBands({ pieces: pieces, exclusions: excl, needed: needed,
                                glyphCostIn: GLYPH_PX / targetPpi });
    }

    function layout() {
      var sceneDepth = num(model.sceneDepth);
      // [WARN] sceneDepth is NULL, not zero, when nothing in the scene has a depth
      // yet, and it is the number we divide by to get px per inch. Guard it
      // explicitly: a zero here gives Infinity rather than an obvious failure.
      var depthIn = sceneDepth == null ? FALLBACK_DEPTH_IN : Math.max(sceneDepth, MIN_DEPTH_IN);

      stageW = stage.clientWidth || rootEl.clientWidth || 320;

      if (lock) {
        // FROZEN, for the length of one gesture. All three numbers are the ones
        // that were already on screen when the handle was grabbed, so nothing
        // resizes under the hand and the box cannot reflow the page. The band is
        // frozen too, so y()/yInv() do not re-cut the pipe and the handle stays
        // under the pointer. See the file header for the freeze reasoning.
        ppi = lock.ppi;
        stageH = lock.stageH;
        bands = lock.bands;
      } else {
        // ONE scale for both axes: whichever of the height budget and the measured
        // width binds first, capped so a short scene does not draw an absurdly fat
        // pipe. Everything below is positioned from this single number.
        //
        // [WARN] THE TV IS DELIBERATELY ABSENT FROM byWidth. Owner decision
        // 2026-07-27. A 65 in screen is 56.65 in wide, so on a 313 px phone stage
        // it drove px per inch to about 5.4 where the height budget allowed 10.6,
        // and the DEPTHS this drawing exists to show were squeezed to half size to
        // pay for screen width nobody is measuring. The screen now runs off both
        // sides at true scale and is cropped by the stage. See the file header for
        // why this is one rule at every width rather than a mobile-only one.
        var budget = heightBudget();
        var byWidth = (stageW - SIDE_PAD_PX * 2) / FIT_WIDTH_IN;
        // The UNBROKEN scale, computed exactly as before, is both the drawing scale
        // when nothing is cut and the gate for whether to cut at all.
        var byHeight0 = (budget - TOP_PAD_PX) / (depthIn + BOTTOM_PAD_IN);
        var ppi0 = Math.min(byHeight0, byWidth, MAX_PPI);
        if (!(ppi0 > MIN_PPI)) ppi0 = MIN_PPI;

        bands = computeBands(depthIn, budget, byWidth, ppi0);
        if (bands.length) {
          // Redraw the shortened mast at the recovered scale. depthIn - removed is
          // the drawn depth once every band is elided; each band adds one GLYPH_PX
          // gap back as fixed pixels, never as inches. See spec section 10.
          var removed = 0, n = bands.length, k;
          for (k = 0; k < n; k++) removed += bands[k].b - bands[k].a;
          ppi = Math.min((budget - TOP_PAD_PX - n * GLYPH_PX) / (depthIn - removed + BOTTOM_PAD_IN),
                         byWidth, MAX_PPI);
          if (!(ppi > MIN_PPI)) ppi = MIN_PPI;
          stageH = TOP_PAD_PX + (depthIn - removed + BOTTOM_PAD_IN) * ppi + n * GLYPH_PX;
        } else {
          ppi = ppi0;
          stageH = TOP_PAD_PX + (depthIn + BOTTOM_PAD_IN) * ppi;
        }
      }

      stage.style.height = px(stageH);
      structure.style.height = px(TOP_PAD_PX);

      layoutPlate();
      layoutMast();
      layoutHead();
      layoutTile();
      layoutLabels();
      refreshHandles();
      renderReason();
    }

    /* ---- ortho part art -----------------------------------------------------
     * ALL OR NOTHING, per spec 7.4. Either both parts draw as real cutouts or
     * both draw as the 2026-07-26 schematic blocks. A cutout next to a schematic
     * rectangle is the collage that ruling refused, and missing cutouts are a
     * permanent steady state, not a gap waiting to close: LCD-MID-CHB and
     * 33CE004HEAD are owner-confirmed to stay without one.
     *
     * The images are decoded ONCE per URL and cached. layout() runs on every
     * pointermove, so nothing in the layout path may touch the network or wait
     * on a promise; it only ever reads naturalWidth off an already-decoded image.
     */
    var artCache = {};        // url -> {img, ready, failed}
    var artPending = false;

    function artEntry(url) {
      if (!url) return null;
      var hit = artCache[url];
      if (hit) return hit;
      var e = { img: new Image(), ready: false, failed: false };
      // The cutouts are on the Files CDN, a different origin from theme assets.
      e.img.crossOrigin = 'anonymous';
      e.img.onload = function () {
        e.ready = !!(e.img.naturalWidth && e.img.naturalHeight);
        e.failed = !e.ready;
        // Re-lay out once the picture exists. Guarded so a burst of loads
        // collapses into one relayout rather than one per image.
        if (!artPending) {
          artPending = true;
          (window.requestAnimationFrame || setTimeout)(function () {
            artPending = false;
            layout();
          }, 0);
        }
      };
      e.img.onerror = function () { e.failed = true; };
      e.img.src = url;
      artCache[url] = e;
      return e;
    }

    /**
     * The cutouts to draw this frame, or null to use the schematic blocks.
     * Returns {plate, head} of decoded images.
     */
    function partArt() {
      var pu = model.plateOrtho, hu = model.headOrtho;
      if (!pu || !hu) return null;              // all or nothing
      var pe = artEntry(pu), he = artEntry(hu);
      if (!pe || !he) return null;
      if (pe.failed || he.failed) return null;  // a dead URL degrades to schematic
      if (!pe.ready || !he.ready) return null;  // still decoding: schematic this frame
      return { plate: pe.img, head: he.img };
    }

    /** Inches of a cutout's dimension, from its intrinsic pixels at 94 ppi. */
    function artIn(px94) { return px94 / ART_NATIVE_PPI; }

    function layoutPlate() {
      var pb = num(model.plateBottom);
      if (pb == null || !(pb > 0)) { show(plate, false); show(plateArt, false); return; }

      var art = partArt();
      if (art) {
        // Owner ruling: every plate and clamp adapter overlays the pipe top by a
        // flat 0.350 in, so the collar hides the joint. The plate's own image
        // height then places its top edge; nothing here inspects the picture.
        var hIn = artIn(art.plate.naturalHeight);
        var wIn = artIn(art.plate.naturalWidth);
        var bottomIn = pb + ART_EDGE_OVERLAY_IN;
        show(plate, false);
        show(plateArt, true);
        // The cache holds the DECODED image; this element is what the customer
        // sees, so it needs the src too. Assign only on change: writing the same
        // src every pointermove would be a wasted DOM write per frame.
        if (plateArt.getAttribute('src') !== model.plateOrtho) {
          // [DESIGN REVIEW R2 F7 2026-09-22] The decoded cache image carries the
          // cutout's intrinsic pixels (94 ppi), so the attributes are exact, never
          // guessed. Written with the src, so only on change, not per pointermove.
          plateArt.setAttribute('width', String(art.plate.naturalWidth));
          plateArt.setAttribute('height', String(art.plate.naturalHeight));
          plateArt.src = model.plateOrtho;
        }
        plateArt.style.top = px(y(bottomIn - hIn));
        plateArt.style.width = px(Math.max(2, wIn * ppi));
        plateArt.style.height = px(Math.max(2, hIn * ppi));
        return;
      }

      show(plateArt, false);
      show(plate, true);
      plate.style.top = px(y(0));
      plate.style.height = px(Math.max(2, pb * ppi));
      plate.style.width = px(Math.max(12, PLATE_W_IN * ppi));
    }

    function layoutMast() {
      var spec = model.mastSpec;
      var art = window.VMSMastArt;
      var pb = num(model.plateBottom);
      var top = num(model.pipeTop);
      if (top == null) top = pb != null ? pb : 0;

      var specLen = (art && spec && spec.length) ? art.totalInches(spec) : null;
      var pipeBottom = num(model.pipeBottom);
      var fallbackLen = pipeBottom != null ? pipeBottom - top : null;

      // FROZEN SCALE: a plain pipe from the plate down to the pipe bottom, and the
      // pipe bottom follows the drop on every move, so the drawing tracks the hand
      // with no lag at all. The composited canvas cannot: it is memoised on the
      // mast SPEC, and the caller only re-solves the spec on commit, so mid-gesture
      // it is a picture of the last committed length. Showing a stale length next
      // to a live head is the exact defect this freeze exists to remove. Reuse the
      // sprite-failure pipe rather than inventing a second element: it is already
      // the schematic stand-in for this mast and it is already styled.
      if (lock) {
        show(mastSlot, false);
        if (fallbackLen != null && fallbackLen > 0) drawFallbackPipe(top, fallbackLen);
        else hidePipeFallback();
        return;
      }

      if (specLen != null && specLen > 0) {
        hidePipeFallback();
        show(mastSlot, true);
        mastSlot.style.top = px(y(top));
        if (!mastCanvas) {
          mastCanvas = make('canvas', NS + '__cv', mastSlot);
        } else if (mastCanvas.parentNode !== mastSlot) {
          mastSlot.appendChild(mastCanvas);
        }
        // THE PIPE SPANS THE DISTANCE BETWEEN THE TWO PINNED PARTS. It is not cut
        // to its own nominal length. Owner ruling 2026-08-20, off a reported gap
        // between the pipe and a 22HE001 head, and it REVERSES the rule this
        // file's header used to carry. See the header for the full reasoning; the
        // short version is that the plate is pinned to the ceiling and the head is
        // pinned to the drop, so the pipe is the only free element and its job is
        // to fill the space, exactly as a real pipe is cut or adjusted to fit.
        // fallbackLen IS that distance, and reusing it rather than recomputing it
        // is the point: the plain pipe drawn during a drag has always been sized
        // this way, which is why the joint was closed while the pointer was down
        // and opened the moment it came up. The two paths now draw the same length.
        var drawLen = specLen;
        if (fallbackLen != null && fallbackLen > 0 &&
            Math.abs(fallbackLen - specLen) <= MAST_STRETCH_MAX_IN) drawLen = fallbackLen;
        // THE BREAK BANDS, converted from SCENE inches to MAST inches from the
        // ceiling end. render() removes each [fromIn, toIn] of plain pipe and draws
        // a glyph in a fixed GLYPH_PX gap; because the canvas is positioned at
        // y(top) and each removed run maps to the same GLYPH_PX y() collapses it to,
        // the drawn gaps land exactly where every other element expects them. The
        // scale between the two spaces is specLen / drawLen (the 2026-08-20 stretch).
        var mastPerScene = specLen / drawLen;
        var brk = [], brkKey = '', bi;
        for (bi = 0; bi < bands.length; bi++) {
          var bf = (bands[bi].a - top) * mastPerScene, bt = (bands[bi].b - top) * mastPerScene;
          if (bf >= 0 && bt <= specLen && bt > bf) {
            brk.push({ fromIn: bf, toIn: bt, glyphPx: GLYPH_PX });
            brkKey += '|b' + Math.round(bf * 100) / 100 + '-' + Math.round(bt * 100) / 100;
          }
        }
        // Memoised exactly like mountMastArt in the configurator: a worst case mast
        // is about 160 drawImage calls and layout runs on every pointermove. The
        // bands are part of the key, so a build that gains or loses a break redraws.
        var lengthPx = ppi * drawLen;
        var key = JSON.stringify(spec) + '|' + Math.round(lengthPx * 10) / 10 + brkKey;
        if (key === mastKey) return;
        mastKey = key;
        // maxPpi IS THE DERIVED PPI FOR THIS DRAW, not the scene ppi. render()
        // computes min(lengthPx / totalIn, maxPpi), and totalIn is the SPEC length,
        // so passing the scene ppi would cap the draw back to the spec's own length
        // and undo the span above. Handing it the exact ratio makes the cap a no-op
        // and pins the canvas to lengthPx. The girth rides the same ratio, so the
        // pipe can draw up to about 1.4 percent off the scene scale: that is 0.2 px
        // on a typical girth and nobody can see it, whereas the joint it closes was
        // a 0.298 in hole the owner spotted on sight. When the spec already spans
        // the distance this expression IS the scene ppi, so nothing changes.
        art.render(mastCanvas, spec, {
          spriteUrl: opts.spriteUrl,
          lengthPx: lengthPx,
          maxPpi: lengthPx / specLen,
          vertical: true,
          breakBands: brk
        }).catch(function (err) {
          // A missing or blocked sprite must never take the scene down: drop back to
          // the schematic pipe and say so in the console, not to the customer.
          mastKey = null;
          if (mastCanvas && mastCanvas.parentNode) mastCanvas.parentNode.removeChild(mastCanvas);
          show(mastSlot, false);
          if (fallbackLen != null && fallbackLen > 0) drawFallbackPipe(top, fallbackLen);
          if (window.console) console.warn('vms-elevation-scene: mast art unavailable', err);
        });
        return;
      }

      show(mastSlot, false);
      mastKey = null;
      if (fallbackLen != null && fallbackLen > 0) drawFallbackPipe(top, fallbackLen);
      else hidePipeFallback();
    }

    // Hide every pooled plain-pipe piece and glyph and clear any zigzag clip.
    // Called wherever the composited canvas is showing instead.
    function hidePipeFallback() {
      var i;
      for (i = 0; i < pipePool.length; i++) { show(pipePool[i], false); clearBreakCut(pipePool[i]); }
      for (i = 0; i < glyphPool.length; i++) show(glyphPool[i], false);
    }

    // The plain pipe, in TWO roles: the sprite-failure fallback and the mast drawn
    // while a gesture has the scale frozen. Owner rev 2.1: when the frozen bands cut
    // this run the PIPE RUNS THROUGH each break; it splits into n + 1 pieces that
    // each extend one glyph gap G into the adjacent gaps and are clipped to the pipe
    // side of the diagonal break line, leaving only the thin band between the two
    // lines blank, plus n SVG glyphs drawing the two lines. Geometry identical to
    // the canvas glyph so nothing jumps at grab or release. The bands are FROZEN, so
    // the upper pieces and the glyphs hold still while only the last piece follows
    // the drop.
    function drawFallbackPipe(topIn, lenIn) {
      var bottomIn = topIn + lenIn;
      var w = Math.max(3, PIPE_OD_IN * ppi);
      var girth = w, G = GLYPH_PX, B = BREAK_BAND_PX;
      // Only the bands that lie strictly inside THIS run cut it (a drag can shrink
      // the run below a band); the chooser's stubs guarantee pipe on both sides.
      var inB = [];
      for (var bi = 0; bi < bands.length; bi++) {
        if (bands[bi].a > topIn && bands[bi].b < bottomIn) inB.push(bands[bi]);
      }
      var n = inB.length, k;

      // n + 1 pipe pieces. Piece k extends UP into the gap above it (to that gap's
      // top a, k > 0) and DOWN into the gap below it (to that gap's bottom b, k < n),
      // so its box spans the full adjacent gap regions; the clip keeps the pipe on
      // the outer side of each gap's line (below line 2 above, above line 1 below).
      for (k = 0; k <= n; k++) {
        var boxTop = k === 0 ? y(topIn) : y(inB[k - 1].a);
        var boxBottom = k === n ? y(bottomIn) : y(inB[k].b);
        var boxH = boxBottom - boxTop;
        // line centres in the box's LOCAL vertical coords. Gap above is the top G px
        // (line 2 at G/2 + B/2); gap below is the bottom G px (line 1 at H - G/2 - B/2).
        var topCy = k > 0 ? (G / 2 + B / 2) : null;
        var botCy = k < n ? (boxH - G / 2 - B / 2) : null;
        var elm = pipePieceAt(k);
        show(elm, true);
        elm.style.top = px(boxTop);
        elm.style.height = px(Math.max(2, boxH));
        elm.style.width = px(w);
        applyBreakClip(elm, w, boxH, topCy, botCy);
      }
      // n glyphs, one per gap, drawing the two lines.
      for (k = 0; k < n; k++) drawBreakGlyph(glyphAt(k), y(inB[k].a), y(inB[k].b) - y(inB[k].a), girth);
      // Hide any pooled elements this build did not use.
      for (k = n + 1; k < pipePool.length; k++) { show(pipePool[k], false); clearBreakCut(pipePool[k]); }
      for (k = n; k < glyphPool.length; k++) show(glyphPool[k], false);
    }

    // The clip-path that runs a plain-pipe piece THROUGH the break: a diagonal cut
    // along the gap line at the top (keep below line 2) and/or the bottom (keep above
    // line 1). Each line leans off the across axis by BREAK_MARK_SLANT with a
    // negative slope, matching the canvas glyph's on-screen lean. With neither cut
    // the clip is cleared and the piece is a plain rectangle.
    function applyBreakClip(elm, w, boxH, topCy, botCy) {
      if (!(boxH > 0) || (topCy == null && botCy == null)) { clearBreakCut(elm); return; }
      var s = -Math.tan(BREAK_MARK_SLANT);   // dy/dx, the "/" lean the canvas draws
      function pctY(v) { return Math.round(v / boxH * 1e4) / 1e2; }
      var pts = [];
      if (topCy != null) {
        pts.push('0% ' + pctY(topCy + s * (0 - w / 2)) + '%');
        pts.push('100% ' + pctY(topCy + s * (w - w / 2)) + '%');
      } else { pts.push('0% 0%'); pts.push('100% 0%'); }
      if (botCy != null) {
        pts.push('100% ' + pctY(botCy + s * (w - w / 2)) + '%');
        pts.push('0% ' + pctY(botCy + s * (0 - w / 2)) + '%');
      } else { pts.push('100% 100%'); pts.push('0% 100%'); }
      var poly = 'polygon(' + pts.join(', ') + ')';
      elm.style.clipPath = poly;
      elm.style.webkitClipPath = poly;
    }

    function clearBreakCut(elm) {
      elm.style.clipPath = '';
      elm.style.webkitClipPath = '';
    }

    // One SVG glyph in a gap: the TWO break lines, each crossing the pipe, tilted
    // BREAK_MARK_SLANT off the across axis and long enough to poke past the pipe
    // sides. Same lines as the canvas, in the vertical pipe's on-screen orientation
    // (across = SVG x, along the gap = SVG y). Rebuilt only when the girth or gap
    // changes, so a drag does not touch it.
    function drawBreakGlyph(el, topPx, gapH, girthPx) {
      var yHalf = (BREAK_MARK_SPAN / 2) * girthPx;          // 0.8 girth across, either side
      var xHalf = yHalf * Math.tan(BREAK_MARK_SLANT);       // along the gap, either side
      var svgW = Math.ceil(2 * yHalf) + 4;
      el.style.top = px(topPx);
      el.style.width = px(svgW);
      el.style.height = px(gapH);
      el.setAttribute('width', svgW);
      el.setAttribute('height', Math.round(gapH));
      var key = svgW + 'x' + Math.round(gapH) + 'x' + Math.round(girthPx);
      if (el._key !== key) {
        el._key = key;
        while (el.firstChild) el.removeChild(el.firstChild);
        var cx = svgW / 2;
        [gapH / 2 - BREAK_BAND_PX / 2, gapH / 2 + BREAK_BAND_PX / 2].forEach(function (cy) {
          // "/" lean: top-right (cx + yHalf, cy - xHalf) to bottom-left (cx - yHalf, cy + xHalf).
          var ln = document.createElementNS(SVGNS, 'line');
          ln.setAttribute('x1', cx + yHalf); ln.setAttribute('y1', cy - xHalf);
          ln.setAttribute('x2', cx - yHalf); ln.setAttribute('y2', cy + xHalf);
          el.appendChild(ln);
        });
      }
      show(el, true);
    }

    function layoutHead() {
      var mountD = num(model.mountDepth);
      head.value = mountD;
      // Clear the grip readout on the way out: this early return skips the "NN in"
      // rewrite below, so a stale value would otherwise survive in the hidden grip.
      if (mountD == null) { head.grip.val.textContent = ''; show(headEl, false); return; }
      show(headEl, true);

      // THE DRAWN EXTENTS, and since the owner's correction of 2026-07-28 they are
      // the whole TV: one 16:9 rectangle off the cabinet height, an inch taller and
      // about 1.8 in wider than the bare screen this used to draw. Nothing in this
      // function had to change for that, which is the point of the module boundary:
      // the geometry decides what the rectangle IS and this only draws it. The
      // rectangle stays centred on the mount (the CSS centres it on this box, and
      // both dimensions grow symmetrically), so the head still sits at its middle.
      var tvH = num(model.tvHeight);
      var tvW = num(model.tvWidth);
      var hasTV = tvH != null && tvW != null && tvH > 0 && tvW > 0;
      // Placed TV shift (SPEC-THEME-tv-placement-2026-10-12.md 2.2; notes assets/vms-tv-lookup.notes.md).
      var shiftIn = hasTV && !model.horizontal && num(model.tvTop) != null && num(model.tvBottom) != null ? mountD - (model.tvTop + model.tvBottom) / 2 : 0;
      if (!(Math.abs(shiftIn) >= 1e-6)) shiftIn = 0;
      var faceIn = hasTV && model.horizontal && num(model.tvShift) ? model.tvShift * (model.tvTopEdge === 'left' ? -1 : model.tvTopEdge === 'right' ? 1 : 0) : 0;

      var art = partArt();
      var artW = 0, artH = 0;
      if (art) {
        artW = artIn(art.head.naturalWidth) * ppi;
        artH = artIn(art.head.naturalHeight) * ppi;
      }

      var blockW = Math.max(14, HEAD_W_IN * ppi);
      var blockH = Math.max(8, HEAD_H_IN * ppi);
      var horizontal = !!model.horizontal;
      var tvWpx = hasTV ? tvW * ppi : 0;
      var tvHpx = hasTV ? tvH * ppi : 0;
      var shiftPx = shiftIn * ppi, facePx = faceIn * ppi;
      var boxW, boxH;
      if (horizontal) {
        // FACE-DOWN LAYOUT (owner ruling 2026-08-26). The head art hangs ABOVE the
        // drop (its bottom edge anchored ON it) and the flat TV box hangs BELOW.
        // The wrapper stays centred on the drop, so its half-height must cover the
        // TALLER of the two sides for the hit box to hold both. See the anchoring
        // set on tvEl and the art below.
        boxW = Math.max(blockW, tvWpx + Math.ceil(2 * Math.abs(facePx)), art ? artW : 0);
        boxH = 2 * Math.max(art ? artH : blockH, tvHpx);
      } else {
        boxW = hasTV ? Math.max(blockW, tvWpx) : blockW;
        boxH = hasTV ? Math.max(blockH, tvHpx + 2 * Math.abs(shiftPx)) : blockH;
        // The cutout can be WIDER than the TV: 33CE008HEAD is 43.86 in against a
        // 65 in screen's 56.65 in, but on a small screen it wins outright. The box
        // is the hit area, so it has to cover whatever is actually drawn.
        if (art) { boxW = Math.max(boxW, artW); boxH = Math.max(boxH, artH); }
      }

      // The whole object is the hit area, TV included. Grabbing the TV to move the
      // TV is the right mental model, and a thin grip strip is not.
      headEl.style.top = px(y(mountD));
      headEl.style.width = px(boxW);
      headEl.style.height = px(boxH);

      // [RULE 2026-08-23] RIGHT-JUSTIFY THE "Drag" CHIP, CLAMPED TO THE STAGE.
      // Owner: "right justified like the drop ceiling drag box." The chip is a
      // child of this wrapper and the CSS pins it 0.6rem in from the wrapper's
      // right edge. But the wrapper is routinely WIDER than the stage - the TV
      // runs off both edges at true scale (see byWidth) - so that plain inset
      // would push the chip off the visible right edge. Add however far the
      // wrapper overhangs the stage to the inset, so the chip's right edge lands
      // at the stage's right edge (matching the tile grip's 0.6rem) when the box
      // overhangs, and at the wrapper's own right edge otherwise. 6 mirrors the
      // 0.6rem in vms-configurator.css at the theme's 10 px root.
      var gripOverhang = Math.max(0, (stageW / 2 + boxW / 2) - stageW);
      head.grip.el.style.right = px(6 + gripOverhang);

      // Clear any anchoring the OTHER orientation left behind. A vertical head
      // draws its parts centred (CSS defaults, no inline top/transform); a
      // horizontal head sets explicit ones below. Resetting unconditionally is what
      // lets a customer switch heads without a stale offset surviving the switch.
      [tvEl, headArt, headBlock].forEach(function (elm) {
        elm.style.removeProperty('top');
        elm.style.removeProperty('transform');
      });

      if (art) {
        // VERTICAL: centred on the wrapper, whose own centre is already the mount
        // depth. Under the half-height rule the hole pattern is the image's vertical
        // middle, so the centring needs no offset EXCEPT the overlap floor below.
        // HORIZONTAL: the cutout's BOTTOM edge (its plate face = the back of the TV)
        // sits ON the drop, so it is bottom-anchored on the wrapper centre. Owner
        // ruling 2026-08-26.
        //
        // THE OVERLAP FLOOR (owner 2026-09-18, verbatim): "nothing should draw flush
        // against the pipe they should have the overlap". The half-height shortcut is
        // wrong on a head whose VESA centre is off mid-height (22HE001, 22HE021: 0.62
        // in below middle), where the head top would land flush with or a hair below
        // the pipe end. headArtNudgeIn nudges the CUTOUT up so its top sits
        // ART_EDGE_OVERLAY_IN above the pipe end. Only the image moves.
        show(headBlock, false);
        show(headArt, true);
        if (headArt.getAttribute('src') !== model.headOrtho) {
          // [DESIGN REVIEW R2 F7 2026-09-22] Intrinsic size from the decoded cache
          // image, same as plateArt above.
          headArt.setAttribute('width', String(art.head.naturalWidth));
          headArt.setAttribute('height', String(art.head.naturalHeight));
          headArt.src = model.headOrtho;
        }
        headArt.style.width = px(Math.max(2, artW));
        headArt.style.height = px(Math.max(2, artH));
        if (horizontal) {
          // Wrapper centre is boxH / 2 from its top; put the art's bottom there.
          // The socket mouth is 2.575 in up inside the silhouette, so the pipe end
          // at drop - offset is hidden behind the art, which paints after the pipe.
          headArt.style.top = px(boxH / 2 - artH);
          headArt.style.transform = 'translateX(-50%)';
        } else {
          // VERTICAL floor: nudge the cutout UP by the shared helper's inches times
          // ppi. Composed onto the CSS centring transform translate(-50%, -50%); the
          // unconditional reset above clears it so a later non-nudged head is clean.
          // The wrapper, TV, grip, drop dimension and labels do not move.
          var nudgePx = headArtNudgeIn(true, false, artIn(art.head.naturalHeight),
                          num(model.mountDepth), num(model.pipeBottom), ART_EDGE_OVERLAY_IN) * ppi;
          if (Math.round(nudgePx) > 0) {
            headArt.style.transform = 'translate(-50%, calc(-50% - ' + px(nudgePx) + '))';
          }
        }
      } else {
        show(headArt, false);
        show(headBlock, true);
        headBlock.style.width = px(blockW);
        headBlock.style.height = px(blockH);
        if (horizontal) {
          headBlock.style.top = px(boxH / 2 - blockH);
          headBlock.style.transform = 'translateX(-50%)';
        }
      }

      show(tvEl, hasTV);
      if (hasTV) {
        tvEl.style.width = px(tvWpx);
        tvEl.style.height = px(tvHpx);
        if (horizontal) {
          // TOP edge of the flat box sits ON the drop (the wrapper centre at
          // boxH / 2), so the box hangs below it rather than straddling it.
          tvEl.style.top = px(boxH / 2);
          tvEl.style.transform = facePx ? 'translateX(calc(-50% ' + (facePx > 0 ? '+ ' : '- ') + px(Math.abs(facePx)) + '))' : 'translateX(-50%)';
        } else if (shiftPx) {
          tvEl.style.transform = 'translate(-50%, calc(-50% ' + (shiftPx > 0 ? '- ' : '+ ') + px(Math.abs(shiftPx)) + '))';
        }
        cropTV(tvWpx);
        nameTV(tvWpx, tvHpx, blockH, shiftPx);
      }
      head.grip.val.textContent = fmt(mountD) + ' in';
    }

    /**
     * Name the screen, quietly, ON the screen.
     *
     * Cropped at true scale the band has no ends and no corners, so nothing about
     * it says "television" (browser pass 2026-07-27: "a pipe hanging into a
     * two-tone box"). One short caption inside the bottom rule fixes that. The
     * BOTTOM, not the top: the mast and everything the part labels point at is
     * above the head, so the underside of the screen is the one part of this
     * drawing nothing else ever occupies.
     *
     * [WARN] NOT MEASURED, EVER. This runs on every pointermove, so both the fit
     * tests below are arithmetic against constants that mirror the stylesheet. A
     * getComputedStyle or an offsetWidth here would put a forced reflow on the
     * drag path, which is the cost the frozen scale exists to avoid.
     *
     * [WARN] THE NUMBER IS HANDED DOWN NOW, AND IT USED TO BE DERIVED. It was the
     * DIAGONAL of the rectangle already drawn, which was exactly the nominal size for
     * as long as that rectangle was the pure 16:9 SCREEN. Since the owner's correction
     * of 2026-07-28 the drawn rectangle is a 16:9 box off the CABINET height, and the
     * diagonal of that is about 2 in larger: a 70 in TV captioned itself 72. So the
     * model echoes the nominal size (model.tvSize) and this prints it. The derivation
     * survives ONLY as a fallback for a standalone caller that hands over drawn
     * extents and no size, and for such a caller it means what it always meant.
     * Do NOT re-derive the size by subtracting the bezel allowance here: this file
     * deliberately holds no copy of that constant. Printing only, like fmt(): nothing
     * is decided from it.
     */
    function nameTV(wPx, hPx, blockH, shiftPx) {
      // Vertical: the caption has to sit clear of whichever of the head block and
      // the grip chip reaches further down from the screen's middle.
      var room = hPx / 2 - (shiftPx || 0) - Math.max(blockH / 2, GRIP_H_PX / 2);
      var fits = room >= TV_NAME_INSET_PX + TV_NAME_H_PX + TV_NAME_CLEAR_PX &&
        Math.min(wPx, stageW) >= TV_NAME_MIN_W_PX;
      show(tvName, fits);
      if (!fits) return;
      var tp = model.tvPlacement || null;
      var nominal = tp && tp.source === 'catalog' && num(tp.screenSizeIn) != null && tp.screenSizeIn > 0 ? tp.screenSizeIn : num(model.tvSize);
      if (nominal == null) nominal = Math.sqrt(wPx * wPx + hPx * hPx) / ppi;
      tvName.textContent = tp && tp.source === 'manual' ? 'Your TV' : String(Math.round(nominal)) + ' inch TV';
      // Belt and braces on the no-overflow rule: the TV is routinely wider than
      // the stage, so a percentage max-width here would mean nothing.
      tvName.style.maxWidth = px(Math.max(0, Math.min(wPx, stageW) - 8));
    }

    /**
     * A screen wider than the stage is CROPPED, not shrunk (owner decision), so it
     * has to read as a drawing that continues past the view rather than as a
     * rectangle that failed to render. Two cues do that, both cheap:
     *
     *  1. NEITHER END IS DRAWN. A box showing one vertical edge and not the other
     *     reads as broken; one showing neither reads as cropped by the frame,
     *     which is exactly what has happened. The --wide class drops the left and
     *     right borders and the corner radius, so the top and bottom rules run
     *     clean out of both sides of the stage.
     *  2. THE LAST FEW PIXELS OF THE FILL FADE. The ordinary "there is more this
     *     way" cue, and the one thing that distinguishes a cropped screen from a
     *     screen that happens to fit exactly: when it fits, both ends are drawn
     *     and crisp.
     *  3. THE BAND IS NAMED. See nameTV(). Cues 1 and 2 say "this continues"; only
     *     the caption says what it is that continues.
     *
     * [WARN] THE MASK GOES ON THE FILL, NEVER ON tvEl. A mask applies to the whole
     * element, borders included, so on tvEl it faded out cue 1 at exactly the two
     * edges cue 1 exists to explain. That is the defect the 2026-07-27 browser
     * pass found: two 1 px rules that dissolve at the sides leave a grey wash with
     * no reading at all. The rules are on the parent and the fade is on the child,
     * and those two facts have to stay apart.
     *
     * [WARN] THE GRADIENT IS MEASURED IN THE TV'S OWN PIXELS, NOT IN PERCENTAGES.
     * The part of a cropped TV that needs fading is not its own end, which is off
     * screen and invisible, but the point where it crosses the stage edge. That
     * point is `over` px in from its own left end and `over` px in from its right,
     * and only the JS knows `over`. The fill spans tvEl's padding box, and --wide
     * has already taken the left and right borders off, so the fill's own width is
     * tvPx and these coordinates land where they say they do.
     *
     * A browser with no mask support degrades to cues 1 and 3, which still read as
     * a crop. No explanatory copy is added beyond the caption: a customer looking
     * at a named screen that runs off both sides of a box does not need to be told
     * it was cropped, and the elevation's job is the depths.
     */
    function cropTV(tvPx) {
      var over = (tvPx - stageW) / 2;
      if (!(over > 1)) {
        tvEl.classList.remove(NS + '__tv--wide');
        tvFill.style.removeProperty('-webkit-mask-image');
        tvFill.style.removeProperty('mask-image');
        return;
      }
      tvEl.classList.add(NS + '__tv--wide');
      // Two fades have to fit inside the VISIBLE window with room to spare, or the
      // gradient stops run backwards and the browser collapses them into a hard
      // edge, which is the broken look this is here to avoid.
      var fade = Math.min(TV_FADE_PX, stageW / 6);
      var g = 'linear-gradient(to right, transparent 0, transparent ' +
        Math.round(over) + 'px, #000 ' + Math.round(over + fade) +
        'px, #000 ' + Math.round(tvPx - over - fade) +
        'px, transparent ' + Math.round(tvPx - over) + 'px)';
      tvFill.style.setProperty('-webkit-mask-image', g);
      tvFill.style.setProperty('mask-image', g);
    }

    function layoutTile() {
      var t = num(model.tileDepth);
      tile.value = t;
      if (!model.hasDropCeiling || t == null) { show(tileEl, false); return; }
      show(tileEl, true);
      tileEl.style.top = px(y(t));
      // Write the value FIRST: it changes the chip's rendered width, and the fit
      // test below measures that chip, so a stale number would measure wrong.
      tile.grip.val.textContent = fmt(t) + ' in';

      // Decide whether the left "Drop ceiling" label fits beside the dashed rule
      // and the chip, or whether it has to be dropped and its name carried on the
      // chip instead. THE FALLBACK IS DROP-RATHER-THAN-CRUSH, the owner's own
      // named behaviour and the same discipline the part labels use above.
      //
      // [RULE] This label CANNOT collide with a part label by construction: the
      // part labels already keep clear of the tile band on purpose (that is what
      // TILE_ROW_H_PX exists for), so anything living inside the band is in
      // territory the gutter labels have already vacated. It therefore needs no
      // leader line - it sits directly on the thing it names.
      //
      // [RULE] ASK THE BROWSER FOR THE DASHED LINE'S WIDTH, DO NOT ARITHMETIC IT.
      // The first cut of this computed the rule's width as clientWidth minus the
      // label, minus the chip, minus two 6 px gaps and a 12 px padding, and it
      // was WRONG BY ABOUT 1.2 px in the tight direction, every time: 6 and 12
      // are the 0.6rem gap and the 0 0.6rem padding at a 10 px root, and THIS
      // THEME'S ROOT MEASURES ABOUT 10.5 px, so the real gap is 6.3 and the real
      // padding 12.6. Measured on dev at the crossover: the formula said the rule
      // would be 56.59 px and the rendered rule was 55.31 px, which is a floor
      // that quietly does not hold. Every one of those constants disappears if we
      // simply lay the row out in its wide state and read the rule back, and then
      // the number we gate on IS the number on screen. Sibling of the standing
      // lesson that this theme's root is not the documented 62.5%.
      var fits = tileLabelShown;

      if (lock) {
        // [RULE] THE DECISION IS FROZEN FOR THE LENGTH OF A GESTURE, exactly the
        // way the scale is, and for the same reason. The chip's width tracks the
        // number printed on it, so a build sitting near the threshold would flip
        // the label on and off under the hand as the customer dragged through
        // 9.5, 10.0, 12.5. Reuse the answer from before the grab; the release
        // unlocks and layout() decides again on a settled value. This also holds
        // the occupancy answer steady: a gesture DROPS the part labels, so a
        // label hidden because a coupler sat on it would otherwise pop back the
        // instant the customer grabbed the handle.
      } else if (!tileEl.clientWidth) {
        // Degenerate guard: before first layout, or while an ancestor is
        // display:none, clientWidth reads 0. Treating that as "does not fit"
        // would drop the label on the very first paint, so assume it fits. Both
        // halves of the state are still written below, which is what stops the
        // label and the renamed chip from ever saying the name twice at once.
        // layout() runs again on resize and on every model change.
        fits = true;
      } else {
        // MEASURE THE ROW IN ITS WIDE-STATE FORM, always, whatever is on screen
        // right now: label shown and the chip reading "Drag". Measuring the chip
        // while it still carries the fallback name would ask a different and
        // wider question every time the answer was already "no", which is how a
        // fallback gets stuck on at widths that would hold the label. There is no
        // paint between here and the show() below (layout is synchronous, it
        // never yields), so nothing flickers even when the answer is "hide it".
        tile.grip.txt.textContent = 'Drag';
        show(tileLabel, true);
        // Reading a rect here forces the layout we just asked for, which is the
        // point: the number below is the laid-out dashed line, with the label in
        // place and the chip at its widest. .vmscfg-scene__tilelabel is
        // flex: 0 0 auto and the chip does not wrap, so all of the row's
        // shrinkage lands on the rule (flex: 1 1 auto) and this width is the
        // whole answer.
        fits = tileRule.getBoundingClientRect().width >= TILE_LABEL_MIN_RULE_PX;
      }

      setTileLabelShown(fits);
    }

    /**
     * The one place the label and the chip's word are written, together.
     *
     * THE FALLBACK RENAME: when the left label is dropped the chip's word carries
     * the name; otherwise the chip reads its usual "Drag". The drag affordance is
     * NOT lost when the word changes, since the bars glyph, the ns-resize cursor
     * and the scene hint paragraph all still say it, and this is the owner's own
     * named fallback.
     *
     * [RULE] THE TWO WRITES LIVE IN ONE FUNCTION ON PURPOSE. They are two halves
     * of one state, and there are now two callers deciding it (the width test in
     * layoutTile and the occupancy downgrade in layoutLabels). Written apart, a
     * caller that set one and not the other would put "Drop ceiling" on the row
     * twice, or leave it off the row entirely.
     *
     * [NOTE, MEASURED, DO NOT "FIX" IT] in the fallback state the string is in the
     * DOM TWICE, because show() only sets display:none and the hidden label keeps
     * its text. On screen it appears exactly once, which is the property that
     * matters, and BOTH nodes are aria-hidden while the accessible name comes from
     * the handle's own aria-label, so nothing reads it twice either. Blanking the
     * hidden label's text would buy a tidier DOM and cost a real failure mode: the
     * measure branch in layoutTile sizes that same node, so a clear that is ever
     * not restored leaves a label measuring zero, permanently "fitting", and never
     * coming back. Any test of this asserts the VISIBLE occurrence, not a
     * textContent count on the row.
     */
    function setTileLabelShown(on) {
      tileLabelShown = on;
      show(tileLabel, on);
      tile.grip.txt.textContent = on ? 'Drag' : copy('tileLabel');
    }

    // ---- part labels -------------------------------------------------------
    // Leader lines with an arrowhead, naming what each drawn part is. Owner
    // request 2026-07-27. Read the file header first: the side split is the
    // collision strategy and the wording is generic on purpose.

    /**
     * [WARN] MIRRORS vms-mast-art's own pieceLength(). It has to: the arrows are
     * positioned by walking the same cursor that paint() walks, so if that walk
     * ever changes, every leader below the first joint points at the wrong band.
     */
    function pieceInches(p) {
      if (!p) return 0;
      if (p.kind === 'tele') {
        var c = num(p.current), lo = num(p.collapsed), hi = num(p.extended);
        if (c == null) c = lo != null ? lo : 0;
        if (lo != null && c < lo) c = lo;
        if (hi != null && c > hi) c = hi;
        return c;
      }
      return num(p.length) != null ? p.length : 0;
    }

    /**
     * Put "<SKU> <name>" at the front of a forms list, or hand the list back
     * untouched when there is no SKU to put there.
     *
     * Owner ask 2026-08-20: the drawing should name the parts a customer is
     * actually buying rather than their categories. This is deliberately a
     * PREPEND onto the existing wording instead of a replacement, because the
     * forms list is a step-down ladder: the named form is tried first and, on a
     * stage too narrow to fit it, the label falls back through exactly the
     * wording it used before this change and finally to "Plate" / "Head". So a
     * phone loses the SKU rather than losing the label, which is the same
     * behaviour narrow stages always had.
     *
     * [WARN] A MISSING SKU MUST NOT PRODUCE "undefined ceiling plate". Every
     * caller passes a model field that is null whenever the part is unresolved,
     * and a coupler's SKU is null if the coupler product ever leaves the pool,
     * so the guard is load-bearing rather than defensive habit.
     */
    function withSku(sku, name, forms) {
      if (!sku || !name) return forms;
      return [sku + ' ' + name].concat(forms);
    }

    /**
     * [DESIGN REVIEW R2 F9 2026-09-22] Write a chip's wording. When the form
     * starts with the part's SKU (the withSku form), the SKU goes in its own
     * span with translate="no", so browser auto-translate cannot garble a part
     * number while the words after it still translate. Every other form is plain
     * text, exactly as before. The rendered text is identical either way, so the
     * width measuring in layoutLabels is unchanged.
     */
    function setChipText(chip, text, sku) {
      if (sku && text.indexOf(sku + ' ') === 0) {
        chip.textContent = '';
        var s = document.createElement('span');
        s.setAttribute('translate', 'no');
        s.textContent = sku;
        chip.appendChild(s);
        chip.appendChild(document.createTextNode(text.slice(sku.length)));
        return;
      }
      chip.textContent = text;
    }

    /**
     * Every part worth naming, top to bottom, with the depth its arrow points at
     * and the half width of the thing being pointed at.
     *
     * `prio` is the DROP ORDER when something cannot fit: lower survives. The head
     * and the plate are what a customer is choosing between, the telescoping
     * section is the one that adjusts, and a coupler is the least informative
     * label on the drawing, so it goes first.
     *
     * `forms` is the wording, LONGEST FIRST. layoutLabels measures the chip and
     * steps down this list until it fits, so the order is the whole contract: put
     * the fullest wording at index 0 and never leave a longer form after a shorter
     * one. `pair` marks the two the owner phrased in parallel, which step down
     * together. See the label sections of the file header.
     */
    function labelParts(withCouplers, gripLeftOver) {
      var out = [];
      var pb = num(model.plateBottom);
      if (pb != null && pb > 0) {
        out.push({
          depth: pb / 2,
          half: Math.max(12, PLATE_W_IN * ppi) / 2,
          side: 'l', prio: 1, pair: true,
          sku: model.plateSku,
          forms: withSku(model.plateSku, model.plateTypeLabel,
                         ['Your ceiling plate', 'Ceiling plate', 'Plate'])
        });
      }

      var spec = model.mastSpec;
      var art = window.VMSMastArt;
      var top = num(model.pipeTop);
      if (top == null) top = pb != null ? pb : 0;
      if (art && spec && spec.length) {
        var add = num(art.COUPLER_NET_ADD) != null ? art.COUPLER_NET_ADD : 1.5;
        var halfPipe = Math.max(3, PIPE_OD_IN * ppi) / 2;
        var halfCplr = Math.max(3, COUPLER_OD_IN * ppi) / 2;
        // The scale mast-spec inches ride to become scene inches, so a piece's
        // scene span can be compared against the band. See mastDrawGeom.
        var geom = mastDrawGeom();
        var scl = geom ? geom.scale : 1;
        var teles = 0, i;
        for (i = 0; i < spec.length; i++) if (spec[i].kind === 'tele') teles++;
        var cursor = 0, len, forms, suffix;
        for (i = 0; i < spec.length; i++) {
          len = pieceInches(spec[i]);
          if (spec[i].kind === 'tele') {
            // Named for what it is rather than numbered, because the adjustable
            // section is the one a customer asks about. Numbered as well only in
            // the case a build ever ships two of them.
            suffix = teles > 1 ? ' ' + (i + 1) : '';
            forms = ['Telescoping pipe' + suffix, 'Telescoping' + suffix];
            // The RANGE, not the setting: "20 to 36 inch" is what the customer
            // ordered and what the box says, whereas the current extension is a
            // number that moves as they drag. Written "X to Y" per the standing
            // range rule; a hyphenated range is not used anywhere in this repo.
            if (num(spec[i].collapsed) != null && num(spec[i].extended) != null) {
              forms.unshift(fmt(spec[i].collapsed) + ' to ' + fmt(spec[i].extended) +
                            ' inch telescoping pipe' + suffix);
            }
          } else {
            forms = ['Pipe ' + (i + 1)];
            // Fixed pipes have no SKU in the spec (specFromBuild carries only
            // kind and length), and they do not need one: the length IS how the
            // catalogue names them, so "6 inch pipe" is both the label and the
            // product. Do not add a lookup to recover the SKU here.
            if (len > 0) forms.unshift(fmt(len) + ' inch pipe');
          }
          // [RULE] A LABEL NEVER ANCHORS INSIDE THE BAND. A piece whose midpoint
          // falls in the removed run would point its leader into the 28 px gap, so
          // the cut piece anchors on the midpoint of its LONGER visible stub
          // instead. Coupler labels are never inside a band (the chooser's stubs
          // keep pipe on both sides of every joint), so only pipes need this.
          var pdepth = top + cursor + len / 2;
          if (bands.length && spec[i].kind !== 'tele') {
            var sStart = top + cursor * scl, sEnd = top + (cursor + len) * scl;
            for (var bk = 0; bk < bands.length; bk++) {
              if (sStart < bands[bk].a && sEnd > bands[bk].b) {   // this piece is the one cut
                pdepth = (bands[bk].a - sStart) >= (sEnd - bands[bk].b)
                  ? (sStart + bands[bk].a) / 2 : (bands[bk].b + sEnd) / 2;
                break;
              }
            }
          }
          out.push({
            depth: pdepth,
            half: halfPipe,
            side: 'r',
            prio: spec[i].kind === 'tele' ? 2 : 10 + i,
            forms: forms
          });
          cursor += len;
          if (i < spec.length - 1) {
            // The sprite draws the coupler centred on the joint plus half the net
            // add, which is the middle of the gap it bridges.
            if (withCouplers) {
              out.push({
                depth: top + cursor + add / 2,
                half: halfCplr,
                side: 'l', prio: 40 + i,
                sku: model.couplerSku,
                forms: withSku(model.couplerSku, 'Coupler', ['Coupler'])
              });
            }
            cursor += add;
          }
        }
      }

      var mountD = num(model.mountDepth);
      if (mountD != null) {
        out.push({
          depth: mountD,
          // [2026-08-23] The "Drag NN in" chip is RIGHT-JUSTIFIED now, so on the
          // LEFT the head's arrow only has to clear the head block plus whatever
          // slice of the chip still crosses the centre line. gripLeftOver is that
          // slice, measured in layoutLabels, and it is 0 in the usual case because
          // a right-justified chip sits out at the head box's right edge. The
          // chip's RIGHTWARD reach is dealt with as a right-gutter handle drop in
          // layoutLabels, not here: this half feeds the LEFT cap and the head's
          // own left-pointing arrow only.
          half: Math.max(Math.max(14, HEAD_W_IN * ppi) / 2, gripLeftOver || 0),
          side: 'l', prio: 0, pair: true,
          sku: model.headSku,
          forms: withSku(model.headSku, 'Mounting head',
                         ['Your mounting head', 'Mounting head', 'Head'])
        });
      }
      return out;
    }

    function labelAt(i) {
      if (!labelPool[i]) {
        labelPool[i] = {
          chip: make('span', NS + '__lab', labelLayer),
          lead: make('span', NS + '__lead', labelLayer)
        };
      }
      return labelPool[i];
    }

    function hideLabels() {
      show(labelLayer, false);
    }

    /**
     * Push apart, then drop. Per side, with the list sorted by the depth it points
     * at:
     *   1. sweep DOWN, moving each chip to the first free slot at or below the
     *      part it names, which resolves every overlap in one pass;
     *   2. sweep back UP from the bottom bound, so a stack that ran off the end of
     *      the stage is pulled back inside it. This can only lower values, and it
     *      re-establishes the gap it borrows from, so neither pass can leave two
     *      chips touching;
     *   3. verify: nothing above the top bound, nothing further than
     *      LABEL_MAX_SHIFT_PX from the part it points at;
     *   4. on failure DROP the least important chip that had to move, and start
     *      again. Never overlap, never draw a leader so long it reads as pointing
     *      at the wrong part.
     * Terminates because every failed round removes exactly one item.
     */
    function placeSide(list) {
      // [2026-08-23] HEIGHT AWARE. A chip is one line or two now (see the wrap
      // test in layoutLabels), so the minimum centre-to-centre spacing between two
      // neighbours is (their two box heights) / 2 + LABEL_GAP_PX, computed per pair
      // rather than from one shared height, and each chip's own half-height sets
      // how close to a stage edge its centre may sit. list[i].h is that box height.
      var i, v, top, bot, gap, ok, worst, wp;
      while (list.length) {
        for (i = 0; i < list.length; i++) {
          top = list[i].h / 2 + 1;
          v = list[i].anchorY;
          if (v < top) v = top;
          if (i > 0) {
            gap = (list[i - 1].h + list[i].h) / 2 + LABEL_GAP_PX;
            if (v < list[i - 1].y + gap) v = list[i - 1].y + gap;
          }
          list[i].y = v;
        }
        for (i = list.length - 1; i >= 0; i--) {
          bot = stageH - list[i].h / 2 - 1;
          v = list[i].y;
          if (v > bot) v = bot;
          if (i < list.length - 1) {
            gap = (list[i].h + list[i + 1].h) / 2 + LABEL_GAP_PX;
            if (v > list[i + 1].y - gap) v = list[i + 1].y - gap;
          }
          list[i].y = v;
        }
        ok = true;
        for (i = 0; i < list.length; i++) {
          if (list[i].y < list[i].h / 2 + 1 - 0.5 ||
              Math.abs(list[i].y - list[i].anchorY) > LABEL_MAX_SHIFT_PX) { ok = false; break; }
        }
        if (ok) return list;
        worst = -1; wp = -1;
        for (i = 0; i < list.length; i++) {
          if (Math.abs(list[i].y - list[i].anchorY) > 0.5 && list[i].prio > wp) {
            wp = list[i].prio; worst = i;
          }
        }
        if (worst < 0) worst = list.length - 1;   // nothing moved and it still failed
        list.splice(worst, 1);
      }
      return list;
    }

    function byAnchor(a, b) { return a.anchorY - b.anchorY; }

    /**
     * The widest a chip on this side may be.
     *
     * [2026-08-23] STILL A WIDTH, but since wrapping landed it is the width of each
     * LINE, not of the whole wording. A form is accepted (in layoutLabels) if it
     * fits on one line within this cap OR wraps to two lines each within it, so the
     * cap now bounds the chip's box width while height absorbs the extra words.
     * This function is unchanged: the geometry it protects (a leader with somewhere
     * to run, arrows stopping clear of the parts) is about the chip's WIDTH, which
     * wrapping does not touch.
     *
     * This replaced a flat 28 percent of the stage on 2026-07-27, and it is the
     * same quantity stated honestly rather than guessed: between the stage edge
     * and the centre line there has to be room for the edge inset, the chip, the
     * clear air after it, the shortest leader still worth drawing, the tip gap and
     * the half width of the widest thing this side's arrows must stop clear of.
     * A fraction cannot know any of that, which is why "Your mounting head" was
     * ellipsised at 1440 px while the pipe labels opposite had 70 px going spare.
     *
     * Left and right differ by a lot and that is the point: the left has to clear
     * the ceiling plate and the head block, the right only a 1.936 in pipe. The
     * head's "Drag NN in" chip used to widen the LEFT here; since 2026-08-23 it is
     * right-justified and no longer crosses the head's centre line on the left, so
     * it is out of this cap on both sides and is handled as a right-gutter handle
     * in layoutLabels instead.
     */
    function gutterCap(parts, side, centreX) {
      var widest = 0, i;
      for (i = 0; i < parts.length; i++) {
        if (parts[i].side === side && parts[i].half > widest) widest = parts[i].half;
      }
      return Math.min(LABEL_GUTTER_MAX, Math.floor(centreX - widest -
        LABEL_TIP_GAP_PX - LABEL_LEAD_MIN_PX - LABEL_LEAD_PAD - LABEL_EDGE_PX));
    }

    function layoutLabels() {
      // [WARN] NO LABELS DURING A FROZEN-SCALE GESTURE, and this is deliberate.
      // The mast is a plain pipe with no drawn parts mid-drag, and the spec behind
      // the names is the one from the last commit, so "Pipe 2" would be pointing
      // at a band that is neither drawn nor the right length any more. Hiding the
      // layer also keeps the per-move path clear of the chip measurement below,
      // which forces a reflow. They come back on release with the real artwork.
      //
      // Re-examined 2026-07-27, when the browser pass noted that every label going
      // at once adds to the "the drawing broke" reading of the freeze. Keeping the
      // plate and the head, whose positions ARE still meaningful mid-drag, was the
      // obvious candidate and it is not cheap: the wording now steps down against a
      // measurement, so any chip on screen means the measure-and-step loop runs on
      // every pointermove, which is exactly the per-move reflow the freeze exists
      // to remove. So they stay hidden, and the two things that DO identify the
      // drawing mid-drag are the ones that cost nothing: the head keeps its "Drag
      // NN in" grip chip and the screen keeps its caption, because both are plain
      // writes to elements that are already positioned.
      if (lock || !(ppi > 0) || stageW < LABEL_MIN_STAGE_W) { hideLabels(); return; }

      // Measured before anything below writes to the DOM, so this read joins the
      // measuring pass below instead of forcing a layout of its own. Off the drag
      // path either way: a locked layout returned above.
      // [2026-08-23] The "Drag NN in" chip is right-justified, so its reach is
      // measured PER SIDE from the SAME offset reads (joined to the measuring pass
      // below, off the drag path either way):
      //   gripLeftOver - how far, if at all, the chip still crosses the head's
      //     centre line to the LEFT. Usually 0: a right-justified chip sits at the
      //     head box's right edge. This is what the left gutter and the head's own
      //     left arrow must clear, so it feeds labelParts.
      //   gripRightX / gripY / gripBandH - the chip's right edge, centre Y and
      //     height, for the right-gutter handle drop after the collision pass. The
      //     chip lands in the RIGHT gutter on a wide (TV-dominated) head box.
      // The head wrapper is centred on the stage centre line, so the chip's
      // stage-space X falls straight out of its offset within that box, read AFTER
      // layoutHead has clamped the chip's right inset to the stage.
      var gripLeftOver = 0, gripRightX = null, gripY = null, gripBandH = 0;
      if (num(model.mountDepth) != null) {
        var gEl = head.grip.el, gw = gEl.offsetWidth;
        if (gw > 0) {
          var gripLeftX = stageW / 2 - head.el.offsetWidth / 2 + gEl.offsetLeft;
          gripRightX = gripLeftX + gw;
          gripLeftOver = stageW / 2 - gripLeftX;
          if (gripLeftOver > 0) gripLeftOver += 2; else gripLeftOver = 0;   // 2 px of arrow air, as the centred chip carried
          gripY = y(num(model.mountDepth));
          gripBandH = gEl.offsetHeight;
        }
      }
      var parts = labelParts(stageW >= LABEL_COUPLER_MIN_W, gripLeftOver);
      if (!parts.length) { hideLabels(); return; }
      var centreX = stageW / 2;
      var capL = gutterCap(parts, 'l', centreX);
      var capR = gutterCap(parts, 'r', centreX);
      var keep = [], i, p, slot;

      // Every chip starts at the LONGEST wording and at width: auto, so the first
      // read is the natural SINGLE-LINE width. The wrap test below only runs for a
      // form whose single line already overflows its cap, which is why a stage wide
      // enough to fit every word on one line never wraps and is unchanged.
      show(labelLayer, true);
      for (i = 0; i < parts.length; i++) {
        p = parts[i];
        p.slot = slot = labelAt(i);
        p.form = 0;
        p.cap = p.side === 'l' ? capL : capR;
        slot.chip.className = NS + '__lab ' + NS + '__lab--' + p.side;
        slot.chip.style.whiteSpace = 'nowrap';
        slot.chip.style.width = 'auto';
        setChipText(slot.chip, p.forms[0], p.sku);
        show(slot.chip, true);
        show(slot.lead, false);
      }
      for (i = parts.length; i < labelPool.length; i++) {
        show(labelPool[i].chip, false);
        show(labelPool[i].lead, false);
      }
      // Nothing measurable means the scene is not rendered yet (a hidden step 4,
      // most likely). Bail before spending reads on unmeasurable boxes.
      if (!(parts[0].slot.chip.offsetHeight > 0)) { hideLabels(); return; }

      // [2026-08-23] A FORM FITS IF IT FITS ON ONE LINE INSIDE THE CAP, OR WRAPS TO
      // AT MOST TWO LINES INSIDE THE CAP WITH NO WORD WIDER THAN THE CAP. Owner:
      // "You could consider wraping the descriptive text to 2 lines." Wrapping
      // trades width for height, which attacks gutterCap's WIDTH limit directly: on
      // one line a form must fit ENTIRELY under the cap, but wrapped the hard floor
      // is only the longest single word. Measured that day at a 390 px phone (left
      // cap about 99): "33HE001 Mounting head" is 152 px on one line but wraps to
      // two lines of 61 and 99 px and clears the cap; at 89 px (375) and 82 px
      // (360) it needs three lines and steps down to "Mounting head" wrapped to two.
      // H1 and H2 are the single-line and two-line BOX heights, from the computed
      // style so the line count costs no reflow of its own.
      var lcs = getComputedStyle(parts[0].slot.chip);
      var lineH = parseFloat(lcs.lineHeight);
      var padBV = parseFloat(lcs.paddingTop) + parseFloat(lcs.paddingBottom) +
                  parseFloat(lcs.borderTopWidth) + parseFloat(lcs.borderBottomWidth);
      var H1 = lineH + padBV, H2 = 2 * lineH + padBV;

      // MEASURE, STEP THE WORDING DOWN, MEASURE AGAIN. Each round is a write batch
      // then a read batch. The nat (single-line) width is read first; only a form
      // whose nat exceeds its cap pays for the second, wrap-mode read.
      //
      // [WARN] +1 px on every width read. getBoundingClientRect is fractional and
      // the width written back is rounded, so a chip that measures 125.4 has to be
      // given 126 or the text it just fitted starts ellipsising again.
      var pass = 0, again = true, pairForm, needWrap;
      while (again && pass < LABEL_FORM_PASSES) {
        again = false;
        pass++;
        for (i = 0; i < parts.length; i++) {
          p = parts[i];
          p.slot.chip.style.whiteSpace = 'nowrap';
          p.slot.chip.style.width = 'auto';
          setChipText(p.slot.chip, p.forms[p.form], p.sku);
        }
        for (i = 0; i < parts.length; i++) {
          parts[i].nat = Math.ceil(parts[i].slot.chip.getBoundingClientRect().width) + 1;
        }
        // Only the forms whose single line overflows the cap get the wrap read:
        // constrain to the cap, let them wrap, and read the line count and whether
        // a word overflowed.
        needWrap = false;
        for (i = 0; i < parts.length; i++) {
          p = parts[i];
          p.wrapped = false;
          if (p.nat <= p.cap) { p.fits = true; continue; }
          p.fits = false;
          p.slot.chip.style.whiteSpace = 'normal';
          p.slot.chip.style.width = px(p.cap);
          needWrap = true;
        }
        if (needWrap) {
          for (i = 0; i < parts.length; i++) {
            p = parts[i];
            if (p.nat <= p.cap) continue;
            var oh = p.slot.chip.offsetHeight;
            var lines = oh <= H1 + 0.5 ? 1 : (oh <= H2 + 0.5 ? 2 : 3);
            var overflow = p.slot.chip.scrollWidth > p.slot.chip.clientWidth + 1;
            p.fits = lines <= 2 && !overflow;
            p.wrapped = p.fits;
          }
        }
        pairForm = 0;
        for (i = 0; i < parts.length; i++) {
          p = parts[i];
          if (p.fits || p.form + 1 >= p.forms.length) continue;
          // The plate and the head are a matched pair in the owner's wording, so
          // they move together. Everything else steps on its own.
          if (p.pair) { if (p.form + 1 > pairForm) pairForm = p.form + 1; continue; }
          p.form++;
          again = true;
        }
        for (i = 0; i < parts.length; i++) {
          p = parts[i];
          if (!p.pair || pairForm <= p.form) continue;
          p.form = Math.min(pairForm, p.forms.length - 1);
          again = true;
        }
      }

      // Size each side's gutter to its widest SURVIVOR, so chips stay uniform down
      // a side and no wider than they need. A single-line survivor needs its nat; a
      // wrapped survivor needs the full cap, since its two lines were laid out to
      // that width. Never wider than the cap either way, so a side where nothing
      // wrapped is byte-for-byte the old single-line drawing. A chip no form of
      // which fits is dropped outright: the stylesheet's ellipsis stays a last-
      // ditch valve for a root font this file cannot predict, not a shipped layout.
      var gutL = 0, gutR = 0, need;
      for (i = 0; i < parts.length; i++) {
        p = parts[i];
        if (!p.fits) continue;
        need = p.wrapped ? p.cap : p.nat;
        if (p.side === 'l') { if (need > gutL) gutL = need; }
        else if (need > gutR) gutR = need;
      }

      // Horizontal geometry second, because the gutter it depends on is only known
      // now: a part whose edge is under its own chip has nowhere to run a leader.
      // The chip's FINAL wording, wrap mode and width are written here, so the box
      // height read straight after is of the thing that actually ships.
      //
      // [WARN] THE TEST IS DIRECTIONAL, not an absolute distance. A left chip's
      // leader must run RIGHTWARDS to its part and a right chip's LEFTWARDS. The
      // old absolute test passed a chip that had overshot its own tip, which draws
      // an arrow pointing back across the drawing at the gutter it came from.
      for (i = 0; i < parts.length; i++) {
        p = parts[i];
        if (!p.fits) continue;
        p.anchorY = y(p.depth);
        if (p.side === 'l') {
          p.tipX = centreX - p.half - LABEL_TIP_GAP_PX;
          p.startX = LABEL_EDGE_PX + gutL + LABEL_LEAD_PAD;
          if (p.tipX - p.startX < LABEL_LEAD_MIN_PX) continue;
        } else {
          p.tipX = centreX + p.half + LABEL_TIP_GAP_PX;
          p.startX = stageW - LABEL_EDGE_PX - gutR - LABEL_LEAD_PAD;
          if (p.startX - p.tipX < LABEL_LEAD_MIN_PX) continue;
        }
        p.slot.chip.style.whiteSpace = p.wrapped ? 'normal' : 'nowrap';
        setChipText(p.slot.chip, p.forms[p.form], p.sku);
        p.slot.chip.style.width = px(p.side === 'l' ? gutL : gutR);
        keep.push(p);
      }
      if (!keep.length) { hideLabels(); return; }

      // Each kept chip now carries its final wording, wrap mode and width, so read
      // its box height: one line or two, PER CHIP. This is the single height the
      // old model kept for the whole stack, now measured for each. A zero means the
      // scene went unrenderable between passes; the same bail the single height had.
      var maxH = 0;
      for (i = 0; i < keep.length; i++) {
        keep[i].h = keep[i].slot.chip.offsetHeight;
        if (keep[i].h > maxH) maxH = keep[i].h;
      }
      if (!(maxH > 0)) { hideLabels(); return; }

      var left = [], right = [];
      for (i = 0; i < keep.length; i++) (keep[i].side === 'l' ? left : right).push(keep[i]);
      left.sort(byAnchor);
      right.sort(byAnchor);
      placeSide(left);
      placeSide(right);

      // [WARN] THE TILE LINE'S GRIP CHIP IS IN THE RIGHT GUTTER TOO. It sits at the
      // right end of the tile handle's row, which is exactly where the pipe labels
      // are, and it is a handle: the collision pass may not push it and must not
      // hide it. So a pipe label that lands across that row is DROPPED, the same
      // rule every other unresolvable overlap gets.
      var tileY = (model.hasDropCeiling && num(model.tileDepth) != null) ? y(model.tileDepth) : null;
      if (tileY != null) {
        // Per-chip height since 2026-08-23: a two-line pipe label needs more clear
        // air from the tile row than a one-line one did.
        right = right.filter(function (q) { return Math.abs(q.y - tileY) >= q.h / 2 + TILE_ROW_H_PX / 2; });

        // [WARN, OWNER-REPORTED 2026-08-23, AND IT CORRECTS A CLAIM THIS FILE AND
        // README BOTH MADE] THE LEFT GUTTER NEEDED THE SAME TREATMENT AND DID NOT
        // HAVE IT. The "Drop ceiling" label added to the left end of the tile row
        // earlier today was documented as unable to collide with a part label by
        // construction, on the grounds that the part labels keep clear of the tile
        // band. THEY DO, BUT ONLY ON THE RIGHT, because until that label existed
        // the only thing in the band was the grip chip at the row's right end. The
        // owner sent a screenshot of a 5.5 in drop ceiling whose dashed line ran
        // straight through the 11AC001 coupler: the coupler's left-gutter label
        // and the new label are both at the stage's left edge at the same y, the
        // label layer paints after the handles, and the coupler chip covered the
        // ceiling label completely. The collision was never impossible; it had
        // only ever been prevented on the other side of the drawing.
        //
        // [RULE] THE PART LABEL WINS AND THE CEILING FALLS BACK TO ITS CHIP, which
        // is the opposite of the right-gutter rule and is deliberate. On the right
        // the occupant is a HANDLE, which cannot be moved or hidden, so the part
        // label has to go. Here the occupant is a part label and the newcomer has
        // somewhere else to be: the fallback built for narrow rows already puts
        // this exact name on the grip chip. Dropping the part label instead would
        // trade one fact for another and lose the coupler's SKU off the drawing;
        // this way the row still says "Drop ceiling 5.5 in" on the chip, the
        // coupler keeps its label, and NOTHING is lost.
        //
        // [NOTE] no x test. Both sit at the stage's left edge by construction, the
        // part chips from LABEL_EDGE_PX and this label from the row's own padding,
        // so a y-band overlap here is an overlap in fact. A width test would be
        // arithmetic pretending to be precision.
        //
        // This only ever turns the label OFF. layoutTile has already decided the
        // width question and written both halves of the state, so a downgrade here
        // is safe in any order and cannot fight it. Under a gesture the labels are
        // dropped entirely and this loop does not run, which is what stops a label
        // hidden by a coupler from popping back the moment the handle is grabbed.
        if (tileLabelShown) {
          for (i = 0; i < left.length; i++) {
            if (Math.abs(left[i].y - tileY) < left[i].h / 2 + TILE_ROW_H_PX / 2) {
              setTileLabelShown(false);
              break;
            }
          }
        }
      }

      // [WARN 2026-08-23] THE HEAD'S "Drag NN in" CHIP IS A RIGHT-GUTTER HANDLE
      // NOW. It used to be centred on the drawing and needed no handling here;
      // right-justified, its right edge reaches toward the right gutter, and on a
      // TV-dominated head box (wider than the stage) it lands exactly where the
      // pipe labels sit. Like the tile grip it is a handle: the collision pass may
      // not move it and must not hide it, so a right pipe label whose row overlaps
      // the chip is DROPPED, the same rule. Gated on the chip actually reaching the
      // gutter, so a narrow head box that keeps the chip near the centre drops
      // nothing.
      if (gripRightX != null && gripY != null &&
          gripRightX >= stageW - LABEL_EDGE_PX - gutR - LABEL_LEAD_PAD) {
        // Per-chip height since 2026-08-23: a two-line pipe label reaches further
        // toward the grip's row than a one-line one did.
        right = right.filter(function (q) { return Math.abs(q.y - gripY) >= q.h / 2 + gripBandH / 2 + LABEL_GAP_PX; });
      }

      var placed = left.concat(right);
      for (i = 0; i < placed.length; i++) {
        p = placed[i];
        p.kept = true;
        p.slot.chip.style.top = px(p.y);
        // ONE rotated rule per label, so the leader points at the true part from
        // wherever the chip ended up. The arrowhead is a CSS border triangle on
        // the rule's far end and rotates with it, which is also why there is no
        // arrow character anywhere in this file.
        var dx = p.tipX - p.startX;
        var dy = p.anchorY - p.y;
        var s = p.slot.lead.style;
        s.left = px(p.startX);
        s.top = px(p.y);
        s.width = px(Math.sqrt(dx * dx + dy * dy));
        s.transform = 'rotate(' + (Math.round(Math.atan2(dy, dx) * 1000) / 1000) + 'rad)';
        show(p.slot.lead, true);
      }
      // Anything dropped loses its chip as well as its leader, whichever pass
      // dropped it: too wide for its side, no room for a leader, or beaten in the
      // vertical collision pass. This sweeps ALL of parts, not just the ones that
      // reached the collision pass, because every one of them was shown above so
      // that it could be measured.
      for (i = 0; i < parts.length; i++) {
        if (!parts[i].kept) { show(parts[i].slot.chip, false); show(parts[i].slot.lead, false); }
      }
    }

    function refreshHandles() {
      [head, tile].forEach(function (h) {
        limits(h);
        if (h.max < h.min) h.max = h.min;    // a collapsed range is still a valid one
        var v = num(h.value);
        h.atMin = v != null && v <= h.min + LIMIT_EPS;
        h.atMax = v != null && h.maxKnown && v >= h.max - LIMIT_EPS;
        // Two decimals, and this is PRINTING, not deciding: the same display grid
        // fmt() uses, applied to the enforced numbers on their way into an
        // attribute. Nothing downstream reads these back, and no value this
        // module EMITS passes through here, so it is not a second quantiser.
        // aria-valuemin="35.279998779296875" is just bad output either way.
        h.el.setAttribute('aria-valuemin', fmt(h.min));
        h.el.setAttribute('aria-valuemax', fmt(h.max));
        if (v == null) {
          // [RULE] aria-valuenow IS REQUIRED ON role=slider AND MAY NOT BE OMITTED
          // (2026-07-28). The empty state used to drop the attribute entirely and
          // lean on aria-valuetext to say "No length set yet.", which reads
          // correctly and is still an invalid slider: a required property of the
          // role was missing, and what a validator or a less forgiving assistive
          // tech does with that is not our call to make. Publishing the MINIMUM is
          // the honest filler, because it is where the handle is actually drawn
          // before anything has been chosen, and aria-valuetext OVERRIDES
          // aria-valuenow in every screen reader, so nothing spoken changes: the
          // empty-state sentence is still what is heard until a value exists. Do
          // not "simplify" this by deleting the valuetext line, and do not put the
          // removeAttribute back.
          h.el.setAttribute('aria-valuenow', fmt(h.min));
          h.el.setAttribute('aria-valuetext', valueText(h));
        } else {
          // [WARN] aria-valuenow is CLAMPED into the advertised range, while the
          // handle is still DRAWN at the raw position. Those two disagreeing is
          // deliberate and it is the lesser evil. #target's input handler is an
          // unclamped live preview on purpose (clamping per keystroke turns "30"
          // into "36" the moment the "3" lands), so mid-typing the value can sit
          // below the floor: typing "30" against a floor of 21 briefly published
          // aria-valuenow="3" with aria-valuemin="21", a value outside its own
          // range, which is a state assistive tech acts on. The picture may show a
          // half-typed number for one keystroke; the accessibility tree may not
          // advertise an impossible one. Found 2026-07-27 alongside the tile-depth
          // clamp, which was the same class of defect from a different cause.
          var ariaV = v;
          if (ariaV < h.min) ariaV = h.min;
          if (h.maxKnown && ariaV > h.max) ariaV = h.max;
          h.el.setAttribute('aria-valuenow', fmt(ariaV));
          h.el.setAttribute('aria-valuetext', valueText(h));
        }
        var atLimit = h.atMin || h.atMax;
        h.el.classList[atLimit ? 'add' : 'remove'](NS + '__handle--atlimit');
      });
    }

    function renderReason() {
      var lines = reasonLines();
      reason.textContent = lines.join(' ');
      show(reason, lines.length > 0);
      // Same gate as the sentence itself, and for the same reason: keyed on the
      // flag, never on reason === 'ceilingNoDepth'. See reasonLines().
      var asking = !!(model.hasDropCeiling && model.needsTileDepth);
      reason.classList[asking ? 'add' : 'remove'](NS + '__reason--ask');
      reason.classList[(!asking && lines.length) ? 'add' : 'remove'](NS + '__reason--limit');
    }

    // ---- resize ------------------------------------------------------------
    function schedule() {
      if (rafId) return;
      rafId = window.requestAnimationFrame(function () {
        rafId = 0;
        layout();
      });
    }

    function onResize() {
      // Only width can change the scale, and layout() writes the stage height, so
      // reacting to a height change would be chasing our own tail.
      var w = stage.clientWidth || rootEl.clientWidth || 0;
      if (w === lastW) return;
      lastW = w;
      schedule();
    }

    if (window.ResizeObserver) {
      ro = new window.ResizeObserver(onResize);
      ro.observe(rootEl);
    } else {
      bind(window, 'resize', onResize);
    }

    // ---- public ------------------------------------------------------------
    function update(m) {
      model = m || {};
      // Keep the head handle's spoken reference in step with the head orientation:
      // a face-down head measures to the BACK of the screen, not its middle. Owner
      // ruling 2026-08-26. Guarded so it is not a DOM write on every update.
      // [NOTE 2026-11-01] a placed TV (upright head) names the center of its mounting holes, owner ruling 3 2026-10-13
      var _tp = model.tvPlacement, _headLbl = 'Length, from the structure down to the ' +
        (model.horizontal ? 'back of the screen' : _tp && _tp.placed === true ? 'center of the TV\'s mounting holes' : 'middle of the screen') + ', in inches';
      if (headEl.getAttribute('aria-label') !== _headLbl) headEl.setAttribute('aria-label', _headLbl);
      lastW = stage.clientWidth || rootEl.clientWidth || 0;
      // Re-read the CSS budget on the next layout. The width cache alone would
      // miss a breakpoint that flipped without the measured width changing, and
      // this path is never the hot one: a locked layout skips the read entirely.
      budgetW = -1;
      layout();
      return inst;
    }

    function destroy() {
      if (ro) { ro.disconnect(); ro = null; }
      // [WARN] _stop() FIRST, then cancel the frame. Stopping a handle unlocks the
      // frozen scale, and unlocking schedules a recovery frame; cancelling first
      // would leave that frame queued to lay out a scene that is about to be
      // pulled out of the document.
      head._stop();
      tile._stop();
      if (rafId) { window.cancelAnimationFrame(rafId); rafId = 0; }
      lock = null;
      labelPool = [];
      for (var i = 0; i < listeners.length; i++) {
        listeners[i][0].removeEventListener(listeners[i][1], listeners[i][2], listeners[i][3]);
      }
      listeners = [];
      mastCanvas = null;
      mastKey = null;
      if (scene.parentNode) scene.parentNode.removeChild(scene);
    }

    var inst = { update: update, destroy: destroy, root: scene };
    layout();
    return inst;
  }

  window.VMSElevationScene = {
    mount: mount,
    // Exposed for the configurator's diag panel and for anyone checking the wiring
    // against the scale rules, never for tuning at a call site.
    _internals: {
      MAX_PPI: MAX_PPI,
      DEFAULT_HEIGHT_PX: DEFAULT_HEIGHT_PX,
      HEIGHT_VAR: HEIGHT_VAR,
      TOP_PAD_PX: TOP_PAD_PX,
      TOUCH_HOLD_MS: TOUCH_HOLD_MS,
      TOUCH_SLOP_PX: TOUCH_SLOP_PX,
      COARSE_MULT: COARSE_MULT,
      KEY_IDLE_MS: KEY_IDLE_MS,
      // The break chooser and the depth mapping are pure functions, exported so
      // they are unit-testable in Node with no DOM. Their constants come with them
      // so a test asserts against the shipped values rather than a copy.
      chooseBreakBands: chooseBreakBands,
      chooseBreakBand: chooseBreakBand,
      mapY: mapY,
      mapYInv: mapYInv,
      headArtNudgeIn: headArtNudgeIn,
      BREAK_PPI_MIN: BREAK_PPI_MIN,
      BREAK_PPI_TARGET: BREAK_PPI_TARGET,
      MIN_BREAK_IN: MIN_BREAK_IN,
      GLYPH_PX: GLYPH_PX,
      STUB_IN: STUB_IN,
      TILE_CLEAR_IN: TILE_CLEAR_IN,
      TV_CLEAR_IN: TV_CLEAR_IN,
      BOTTOM_PAD_IN: BOTTOM_PAD_IN,
      MIN_PPI: MIN_PPI
    }
  };
})();
